import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const TRANSLATIONS = {
  en: {
    "lang.name": "English",
    "lang.short": "EN",

    // Nav
    "nav.dashboard": "Dashboard",
    "nav.calendar": "Calendar",
    "nav.assets": "Assets",
    "nav.journal": "Journal",
    "nav.damage": "Damage",
    "nav.fishing": "Fishing",
    "nav.map": "Photo Map",
    "nav.weather": "Weather",
    "nav.places": "Places",
    "nav.commercial": "Commercial",
    "nav.maintenance": "Maintenance",
    "nav.invoices": "Invoices",
    "nav.documents": "Documents",
    "nav.reports": "Reports",
    "nav.intelligence": "Intelligence",
    "nav.trash": "Trash",
    "nav.manual": "Manual",
    "nav.settings": "Settings",

    "header.subtitle": "OPERATIONAL TRUTH LEDGER",
    "header.home": "Home",

    // Common
    "common.signOut": "Sign out",
    "common.save": "Save",
    "common.saving": "Saving…",
    "common.saveChanges": "Save changes",
    "common.cancel": "Cancel",
    "common.edit": "Edit",
    "common.delete": "Delete",
    "common.keep": "Keep",
    "common.add": "Add",
    "common.new": "New",
    "common.search": "Search",
    "common.apply": "Apply",
    "common.loading": "Loading…",
    "common.all": "All",
    "common.none": "— none —",
    "common.noneLabel": "not specified",
    "common.details": "Details",
    "common.hide": "Hide",
    "common.optional": "optional",
    "common.required": "required",
    "common.notes": "Notes",
    "common.description": "Description",
    "common.actions": "Actions",
    "common.type": "Type",
    "common.status": "Status",
    "common.date": "Date",
    "common.amount": "Amount",
    "common.vendor": "Vendor",
    "common.category": "Category",
    "common.name": "Name",
    "common.owner": "Owner",
    "common.client": "Client",
    "common.from": "from",
    "common.to": "to",
    "common.yes": "Yes",
    "common.no": "No",

    // Role labels
    "role.super_admin": "System Admin",
    "role.owner_director": "Owner / Director",
    "role.skipper": "Skipper",
    "role.accountant": "Accountant",
    "role.operations_admin": "Operations Admin",
    "role.maintenance_user": "Maintenance",

    // Status
    "status.approved": "approved",
    "status.pending_approval": "pending approval",
    "status.rejected": "rejected",
    "status.cancelled": "cancelled",
    "status.draft": "draft",
    "status.protected": "PROTECTED",
    "status.open": "open",
    "status.in_progress": "in progress",
    "status.resolved": "resolved",
    "status.closed": "closed",
    "status.paid": "paid",
    "status.unpaid": "unpaid",
    "status.overdue": "overdue",
    "status.available": "available",
    "status.reserved": "reserved",
    "status.maintenance": "maintenance",
    "status.out_of_service": "out of service",

    // Reservation types
    "reservation.private": "Private",
    "reservation.charter_skippered": "Charter (Skippered)",
    "reservation.excursion": "Excursion",
    "reservation.maintenance_block": "Maintenance Block",

    // Severity
    "severity.low": "low",
    "severity.medium": "medium",
    "severity.high": "high",
    "severity.critical": "critical",

    // Login
    "login.restricted": "// RESTRICTED ACCESS",
    "login.signInToContinue": "Sign in to continue",
    "login.authorisedOnly": "Internal operational tool. Authorised personnel only.",
    "login.email": "EMAIL",
    "login.password": "PASSWORD",
    "login.submit": "SIGN IN",
    "login.authenticating": "AUTHENTICATING…",
    "login.testAccounts": "TEST ACCOUNTS · PWD: LaSovita2026!",
    "login.createAccount": "Create account",
    "login.signInTab": "Sign in",
    "login.signUpTab": "Request access",
    "login.signUpTitle": "Request access to LaSovita Ledger",
    "login.signUpSub": "An administrator will review your request. You will be able to sign in once approved — no automatic access.",
    "login.fullName": "FULL NAME",
    "login.phone": "PHONE (OPTIONAL)",
    "login.passwordMin": "At least 8 characters",
    "login.submitSignUp": "Request access",
    "login.submittingSignUp": "Sending request…",
    "login.signUpSuccess": "Your access request has been submitted. An administrator will review it shortly — you will be able to sign in once approved.",
    "login.backToSignIn": "← Back to sign in",
    "login.awaitingApproval": "Your account is awaiting administrator approval.",
    "login.forgotLink": "Forgot password?",
    "login.forgotLabel": "// PASSWORD RESET",
    "login.forgotTitle": "Reset your password",
    "login.forgotSub": "Enter your account email. If it matches an active account, we'll send a link to reset your password. The link expires in 30 minutes.",
    "login.forgotSubmit": "Send reset link",
    "login.forgotSubmitting": "Sending…",
    "login.forgotSuccessTitle": "Check your inbox",
    "login.forgotSuccessBody": "If an account exists for that email, a reset link has been sent. The link expires in 30 minutes.",
    "reset.label": "// CHOOSE A NEW PASSWORD",
    "reset.title": "Set a new password",
    "reset.subtitle": "Choose a new password for your LaSovita Ledger account. You'll be signed in after saving.",
    "reset.newPassword": "NEW PASSWORD",
    "reset.confirmPassword": "CONFIRM NEW PASSWORD",
    "reset.submit": "Update password",
    "reset.submitting": "Saving…",
    "reset.tooShort": "Password must be at least 8 characters.",
    "reset.mismatch": "Passwords do not match.",
    "reset.doneTitle": "Password updated",
    "reset.doneBody": "Your password has been changed. You can now sign in with your new password.",
    "reset.signInNow": "Sign in now",
    "reset.missingTokenTitle": "Missing or invalid link",
    "reset.missingTokenBody": "This reset link is missing a token. Please request a new one from the sign-in page.",
    "nav.approvals": "Approvals",
    "approvals.subtitle": "ACCESS CONTROL",
    "approvals.title": "Pending access requests",
    "approvals.intro": "Review users who have requested access. Approve to choose their role and enable login, or reject to decline.",
    "approvals.empty": "No pending requests.",
    "approvals.requested": "Requested {date}",
    "approvals.role": "ROLE ON APPROVAL",
    "approvals.approve": "Approve",
    "approvals.reject": "Reject",
    "approvals.confirmReject": "Reject this access request? The user will not be able to log in.",
    "approvals.approved": "Access granted · {name}",
    "approvals.rejected": "Request declined",
    "approvals.pendingBadge": "pending",
    "login.tagline1": "Every reservation.",
    "login.tagline2": "Every log.",
    "login.tagline3": "Every approval.",
    "login.tagline4": "On record.",
    "login.description": "Private operational platform for jointly owned boating operations. Built for dockside decisions, arbitration-grade audit trails, and decades of place intelligence.",
    "login.operationalSystem": "OPERATIONAL TRUTH SYSTEM",

    // Media gallery (generic)
    "gallery.photos": "PHOTOS",
    "gallery.add": "Add photos",
    "gallery.uploading": "Uploading…",
    "gallery.uploaded": "{count} photo(s) uploaded",
    "gallery.uploadFailed": "{count} photo(s) failed to upload",
    "gallery.deleted": "Photo deleted",
    "gallery.empty": "No photos yet.",
    "gallery.captured": "captured",
    "gallery.takePhoto": "Take photo",

    // Holiday rotation
    "holidays.subtitle": "HOLIDAY ROTATION",
    "holidays.intro": "Auto-generated rotation of national holidays and key dates. Rotation flips each year so both owners get a fair share. Admins can override the dates and assigned owner below.",
    "holidays.saved": "Holiday saved",
    "holidays.empty": "No holidays configured — add at least two owner-director users first.",
    "holidays.names.new_year": "New Year",
    "holidays.names.preseren_day": "Prešeren Day",
    "holidays.names.easter_monday": "Easter Monday",
    "holidays.names.may_1": "Labour Day (May 1–2)",
    "holidays.names.statehood_day": "Statehood Day",
    "holidays.names.assumption_day": "Assumption Day",
    "holidays.names.reformation_day": "Reformation Day",
    "holidays.names.all_saints": "All Saints' Day",
    "holidays.names.christmas": "Christmas & Independence Day",

    // Intelligence
    "intel.subtitle": "// INTELLIGENCE LAYER",
    "intel.title": "Operational intelligence",
    "intel.intro": "Aggregated insights across fishing performance, maintenance cadence, and utilization opportunities — updated live as logs and reservations are entered.",
    "intel.tab.heatmap": "FISHING HEATMAP",
    "intel.tab.forecast": "MAINTENANCE FORECAST",
    "intel.tab.utilization": "UTILIZATION",
    "intel.tab.usage": "USAGE",
    "intel.usage.formula": "Usage = days with a journal entry · reservations and maintenance days don't count unless logged as 'on-dock overnight'",
    "intel.usage.empty": "No qualifying journal entries this year yet.",
    "intel.usage.days": "days",
    "intel.usage.ofTotal": "of credited",
    "intel.usage.dockNights": "dock nights",
    "intel.usage.monthly": "MONTHLY USAGE — DAYS",
    "intel.usage.distinctTotal": "DISTINCT FLEET-USE DAYS",
    "intel.usage.bothSameDay": "DAYS WITH ≥2 OWNERS",
    "intel.usage.sharedNudge": "shared days with ≥2 owners on board — credit is split 50/50 so the totals stay fair when you sailed together.",
    "intel.usage.sharedNudgeOne": "shared day with ≥2 owners on board — credit is split 50/50 so the totals stay fair when you sailed together.",
    "intel.heatmap.empty": "No geotagged fishing logs yet. Create a fishing log with a captured location to start seeing hotspots.",
    "intel.heatmap.trips": "trips",
    "intel.heatmap.avgRating": "avg rating",
    "intel.heatmap.topSpecies": "TOP SPECIES",
    "intel.heatmap.latest": "Latest",
    "intel.forecast.empty": "No assets with maintenance history yet.",
    "intel.forecast.lastService": "LAST SERVICE",
    "intel.forecast.daysSince": "DAYS SINCE",
    "intel.forecast.avgInterval": "AVG INTERVAL",
    "intel.forecast.predictedNext": "PREDICTED NEXT",
    "intel.forecast.tickets12mo": "SERVICES (12mo)",
    "intel.forecast.cost12mo": "COST (12mo)",
    "intel.forecast.ticketsTotal": "TOTAL TICKETS",
    "intel.forecast.overdue": "overdue",
    "intel.forecast.onTrack": "on track",
    "intel.forecast.noHistory": "needs history",
    "intel.utilization.title": "YEAR OVERVIEW",
    "intel.utilization.daysReserved": "d reserved",
    "intel.utilization.private": "Private",
    "intel.utilization.charter": "Charter",
    "intel.utilization.maintenance": "Maintenance",
    "intel.utilization.idleTitle": "PRIME-SEASON IDLE WEEKENDS",
    "intel.utilization.idleIntro": "Saturdays & Sundays in Jun–Sep without any reservation. These are revenue opportunities you could fill with charter, excursion, or owner usage.",
    "intel.utilization.noIdle": "All prime-season weekends are booked. Nice.",
    "intel.utilization.moreIdle": "more",
    "common.loading": "Loading…",

    // Maintenance reminder language field
    "maintenance.reminderLanguage": "REMINDER LANGUAGE",
    "maintenance.reminderLanguage.en": "English",
    "maintenance.reminderLanguage.sl": "Slovenščina",

    // Weather
    "weather.section": "HOME-BASE CONDITIONS",
    "weather.title": "Weather · Portorož",
    "weather.loading": "Fetching live conditions…",
    "weather.loadFail": "Weather service unavailable.",
    "weather.updated": "Updated",
    "weather.temp": "AIR TEMP",
    "weather.feelsLike": "Feels like",
    "weather.wind": "WIND",
    "weather.gust": "gust",
    "weather.wave": "WAVE",
    "weather.seaTemp": "SEA TEMP",
    "weather.cloud": "CLOUD",
    "weather.precip": "PRECIP",
    "weather.pressure": "PRESSURE",
    "weather.humidity": "HUMIDITY",
    "weather.sun": "SUN",
    "weather.sunrise": "Sunrise",
    "weather.sunset": "Sunset",
    "weather.uvMax": "Max UV index today",
    "weather.moon": "MOON",
    "weather.moonHint": "Approximate phase — useful for fishing & night-navigation planning.",
    "weather.phase.new_moon": "New moon",
    "weather.phase.waxing_crescent": "Waxing crescent",
    "weather.phase.first_quarter": "First quarter",
    "weather.phase.waxing_gibbous": "Waxing gibbous",
    "weather.phase.full_moon": "Full moon",
    "weather.phase.waning_gibbous": "Waning gibbous",
    "weather.phase.last_quarter": "Last quarter",
    "weather.phase.waning_crescent": "Waning crescent",
    "weather.tides": "TIDES",
    "weather.tidesNone": "No tide events in the next 3 days.",
    "weather.tide.high": "High",
    "weather.tide.low": "Low",
    "weather.forecast7d": "7-DAY FORECAST",


    // Trash (unified)
    "trash.subtitle": "RECYCLE BIN",
    "trash.title": "Trash",
    "trash.intro": "Deleted journals, maintenance tickets and invoices are kept here for 30 days. Restore anytime — after 30 days they are permanently purged automatically.",
    "trash.empty": "Nothing in trash. Great housekeeping.",
    "trash.section.logs": "DELETED JOURNAL ENTRIES",
    "trash.section.tickets": "DELETED MAINTENANCE TICKETS",
    "trash.section.invoices": "DELETED INVOICES",
    "trash.restore": "Restore",
    "trash.restoredToast": "Restored",
    "trash.purgedToast": "Permanently deleted",
    "trash.purgeConfirm": "Permanently delete this entry NOW? This action is irreversible.",
    "trash.purgeNow": "Purge now",
    "trash.purgesOn": "PURGES ON",

    // Invoice AI scan
    "invoices.scan.label": "// AI INVOICE SCAN",
    "invoices.scan.help": "Upload a photo or PDF of the invoice and we'll auto-fill the form with vendor, date, total, VAT and category.",
    "invoices.scan.btn": "Scan invoice",
    "invoices.scan.snap": "Snap receipt",
    "invoices.snapReceipt": "Snap receipt",
    "invoices.scan.scanning": "Reading…",
    "invoices.scanSuccess": "Invoice parsed — please review before saving",

    // Documents view + delete
    "documents.view": "View",
    "documents.deleteConfirm": "Delete this document? This action is permanent.",
    "documents.deletedToast": "Document deleted",
    "documents.noFile": "No file attached to this document",

    // Places edit + delete
    "places.editPlace": "Edit place",
    "places.updatedToast": "Place updated",
    "places.addedToast": "Place added",
    "places.deleteConfirm": "Delete \"{name}\"? This will also remove all reviews. This action is permanent.",
    "places.deletedToast": "Place deleted",

    // Dashboard
    "dashboard.welcome": "// WELCOME",
    "dashboard.welcomeBack": "Good to see you,",
    "dashboard.statusLoading": "Loading fleet status…",
    "dashboard.statusLine": "{assets} assets tracked · {logs} logs on record · {pending} pending approvals",
    "dashboard.newReservation": "New Reservation",
    "dashboard.logTrip": "Log a Trip",
    "dashboard.snapReceipt": "Snap receipt",
    "dashboard.fleet": "FLEET",
    "dashboard.fleetSub": "Active assets",
    "dashboard.awaitingVote": "AWAITING VOTE",
    "dashboard.awaitingVoteSub": "Commercial approvals",
    "dashboard.openTickets": "OPEN TICKETS",
    "dashboard.openTicketsSub": "Maintenance queue",
    "dashboard.missingLogs": "MISSING LOGS",
    "dashboard.missingLogsSub": "Past trips without log",
    "dashboard.upcomingLabel": "UPCOMING",
    "dashboard.upcomingTitle": "Next reservations",
    "dashboard.viewCalendar": "View calendar",
    "dashboard.noUpcoming": "No upcoming reservations. The calendar is clear.",
    "dashboard.dualApproval": "DUAL APPROVAL",
    "dashboard.awaitingDirectors": "Awaiting directors",
    "dashboard.noPending": "No pending approvals. Clear deck.",
    "dashboard.recentActivity": "RECENT ACTIVITY",
    "dashboard.captainsJournal": "Captain's journal",
    "dashboard.usage.label": "USAGE BALANCE",
    "dashboard.usage.title": "Rok vs Borut — {year}",
    "dashboard.usage.subtitle": "Who's used the boat so far this season — at a glance.",
    "dashboard.usage.nights": "Nights aboard",
    "dashboard.usage.trips": "Day trips",
    "dashboard.usage.credited": "Credited days",
    "dashboard.usage.shared": "{n} shared days · split 50/50",
    "dashboard.usage.sharedOne": "{n} shared day · split 50/50",
    "dashboard.usage.even": "Perfectly even",
    "dashboard.usage.ahead": "{name} ahead by {n}",
    "dashboard.usage.empty": "No logged days yet this season.",
    "dashboard.usage.viewMore": "Full breakdown",
    "dashboard.writeup.label": "MAINTENANCE WRAP-UP",
    "dashboard.writeup.title": "Record what was done",
    "dashboard.writeup.intro": "These planned jobs have ended. Note what was actually done so the record stays complete.",
    "dashboard.writeup.endedAgo": "ended {n}d ago",
    "dashboard.writeup.recordBtn": "Record",
    "dashboard.writeup.dialogTitle": "Maintenance write-up",
    "dashboard.writeup.notesLabel": "WHAT WAS DONE",
    "dashboard.writeup.notesPlaceholder": "e.g. boat lift 28/05, zinc change, polishing, antifouling, depth finder transponder + cable, back to water 01/06",
    "dashboard.writeup.notesRequired": "Please write what was done",
    "dashboard.writeup.photosLabel": "FINISHED-WORK PHOTOS (OPTIONAL)",
    "dashboard.writeup.photosDrop": "Add photos of the finished work",
    "dashboard.writeup.saveBtn": "Save write-up",
    "dashboard.writeup.savedToast": "Maintenance write-up saved",
    "dashboard.openJournal": "Open journal",
    "dashboard.noLogs": "No logs yet. Record your first trip.",
    "dashboard.votes": "votes",

    // Calendar
    "calendar.today": "TODAY",
    "calendar.now": "NOW",
    "calendar.wx.clear": "Clear",
    "calendar.wx.partly": "Partly cloudy",
    "calendar.wx.cloudy": "Cloudy",
    "calendar.wx.fog": "Fog",
    "calendar.wx.rain": "Rain",
    "calendar.wx.snow": "Snow",
    "calendar.wx.storm": "Storm",
    "calendar.wx.wind": "wind",
    "calendar.prevMonth": "Previous month",
    "calendar.nextMonth": "Next month",
    "calendar.allAssets": "All assets",
    "calendar.newReservation": "New Reservation",
    "calendar.more": "more",
    "calendar.agendaTitle": "Month agenda",
    "calendar.agendaDays": "day(s) with reservations",
    "calendar.agendaEmpty": "No reservations this month.",
    "calendar.protected": "protected",
    "calendar.add": "New",
    "calendar.viewAgenda": "AGENDA",
    "calendar.viewGrid": "GRID",
    "calendar.legend.pending": "Pending approval",
    "calendar.legend.approved": "Approved commercial",
    "calendar.legend.maintenance": "Maintenance",
    "calendar.legend.notUsed": "Not used (void)",
    "calendar.legend.dayTrip": "Day trip (log)",
    "calendar.legend.onDock": "On-dock stay",
    "calendar.logsOn": "Logs: on",
    "calendar.logsOff": "Logs: off",
    "calendar.logsToggleHint": "Show / hide journal logs on the calendar",
    "calendar.void": "VOID",
    "calendar.legend.ownerPrivate": "{name} · private",
    "calendar.legend.ownerProtected": "{name} · protected",
    "calendar.days.mon": "MON", "calendar.days.tue": "TUE", "calendar.days.wed": "WED",
    "calendar.days.thu": "THU", "calendar.days.fri": "FRI", "calendar.days.sat": "SAT", "calendar.days.sun": "SUN",
    "calendar.detail.asset": "Asset",
    "calendar.detail.dates": "Dates",
    "calendar.detail.client": "Client",
    "calendar.detail.passengers": "Passengers",
    "calendar.detail.price": "Trip price",
    "calendar.detail.requestedBy": "Requested by",
    "calendar.detail.approvals": "APPROVALS",
    "calendar.detail.yourVote": "// YOUR VOTE REQUIRED",
    "calendar.detail.approve": "Approve",
    "calendar.detail.reject": "Reject",
    "calendar.detail.notePlaceholder": "Optional note (visible in audit trail)…",
    "calendar.detail.confirmDelete": "Delete this reservation permanently? This cannot be undone — the record disappears from the calendar but remains in the audit trail.",
    "calendar.detail.markNotUsed": "Mark not used",
    "calendar.detail.markUsed": "Mark as used",
    "calendar.detail.notUsedPrompt": "Record that the boat was not used for this reservation. It stays on the calendar (and keeps blocking the dates) but is shown as void.",
    "calendar.detail.notUsedReasonPlaceholder": "Reason (optional) — e.g. bad weather",
    "calendar.detail.confirmNotUsed": "Mark not used",
    "calendar.detail.markedNotUsed": "Reservation marked as not used",
    "calendar.detail.markedUsed": "Reservation marked as used",
    "calendar.detail.noReason": "No reason given",
    "calendar.detail.yesDelete": "Yes, delete",
    "calendar.detail.linkedLogs": "ATTACHED DAILY LOGS",
    "calendar.detail.noLinkedLogs": "No daily log has been attached to this reservation yet.",
    "calendar.detail.addLog": "Add daily log",
    "calendar.detail.history": "EDIT HISTORY",
    "calendar.detail.noHistory": "No audit events yet.",
    "calendar.detail.confirmDeleteLog": "Delete this log permanently? The entry is removed from the journal but remains in the audit trail.",
    "audit.action.create": "created",
    "audit.action.update": "edited",
    "audit.action.delete": "deleted",
    "audit.action.vote_approved": "approved",
    "audit.action.vote_rejected": "rejected",
    "audit.changed": "Changed",

    // Reservation dialog
    "resDialog.new": "New Reservation",
    "resDialog.edit": "Edit Reservation",
    "resDialog.type": "TYPE",
    "resDialog.asset": "Asset",
    "resDialog.startDate": "Start date",
    "resDialog.endDate": "End date",
    "resDialog.duration": "Duration: {days} day(s)",
    "resDialog.owner": "Owner",
    "resDialog.selectOwner": "Select owner",
    "resDialog.markProtected": "Mark as protected term",
    "resDialog.protectedHint": "Cannot be overridden by commercial requests (≥14 consecutive days recommended)",
    "resDialog.fourteenWarning": "Protected terms are typically 14+ consecutive days per the owners' agreement.",
    "resDialog.clientName": "Client name",
    "resDialog.clientContact": "Client contact",
    "resDialog.passengers": "Passengers",
    "resDialog.priceEur": "Trip price (€)",
    "resDialog.commercialWarning": "Commercial bookings require dual-director approval before confirmation.",
    "resDialog.notesPlaceholder": "Optional context for this reservation…",
    "resDialog.conflicts": "CONFLICTS DETECTED ({count})",
    "resDialog.savedReservation": "Save reservation",

    // Assets
    "assets.title": "Assets",
    "assets.subtitle": "FLEET REGISTRY",
    "assets.add": "Add Asset",
    "assets.edit": "Edit asset",
    "assets.new": "Add asset",
    "assets.none": "No assets yet. Add your first vessel.",
    "assets.types.boat": "boat",
    "assets.types.jetski": "jetski",
    "assets.types.trailer": "trailer",
    "assets.fields.type": "TYPE",
    "assets.fields.status": "STATUS",
    "assets.fields.reg": "REGISTRATION",
    "assets.fields.serial": "SERIAL",
    "assets.fields.image": "IMAGE URL (OPTIONAL)",
    "assets.fields.imageOr": "— OR UPLOAD —",
    "assets.fields.uploadPhoto": "Choose from gallery",
    "assets.fields.takePhoto": "Take photo",
    "assets.fields.uploading": "Uploading…",
    "assets.fields.notes": "NOTES",

    // Journal
    "journal.title": "Trip logbook",
    "journal.subtitle": "CAPTAIN'S JOURNAL",
    "journal.newEntry": "New Log Entry",
    "journal.filter.all": "All",
    "journal.filter.captain": "Captain's log",
    "journal.filter.fishing": "Fishing",
    "journal.filter.excursion": "Excursion",
    "journal.filter.charter": "Charter",
    "journal.filter.dockOvernight": "On-dock overnight",
    "journal.empty.title": "No entries yet.",
    "journal.deleteConfirm": "Delete this log entry? It will be kept for 30 days in Trash before being permanently removed.",
    "journal.deletedToast": "Log moved to trash",
    "journal.restoredToast": "Log restored",
    "journal.purgedToast": "Log permanently deleted",
    "journal.purgeConfirm": "Permanently delete this log RIGHT NOW? This action is irreversible.",
    "journal.purgeOn": "purges on",
    "journal.purgeNow": "Purge now",
    "journal.restore": "Restore",
    "journal.trashBtn": "Trash",
    "journal.trashTitle": "DELETED LOGS",
    "journal.trashIntro": "Deleted logs are kept here for 30 days. You can restore them anytime; after 30 days they are permanently purged.",
    "journal.savedToast": "Log entry saved",
    "journal.empty.sub": "Record your first trip — every log builds your place intelligence.",
    "journal.logType.captains_log": "Captain's log",
    "journal.logType.fishing_log": "Fishing log",
    "journal.logType.excursion_log": "Excursion",
    "journal.logType.charter_log": "Charter",
    "journal.logType.dock_overnight": "On-dock overnight",
    "journal.metric.fuel": "FUEL",
    "journal.metric.hrs": "HRS",
    "journal.metric.sea": "SEA",
    "journal.metric.fishing": "FISHING",
    "journal.metric.species": "Species",
    "journal.metric.bait": "Bait",
    "journal.metric.method": "Method",
    "journal.metric.kept": "Kept",
    "journal.metric.released": "Released",
    "journal.metric.rating": "Rating",
    "journal.label.incidents": "Incidents",
    "journal.label.dos": "Do",
    "journal.label.donts": "Don't",
    "journal.label.restaurant": "RESTAURANT",
    "journal.label.bar": "BAR",
    "journal.label.guestExperience": "Guest experience",
    "journal.label.photos": "PHOTOS",
    "journal.untitled": "Untitled trip",

    // Journal dialog
    "logDialog.title": "New log entry",
    "logDialog.logType": "LOG TYPE",
    "logDialog.asset": "Asset",
    "logDialog.date": "Date",
    "logDialog.fromDate": "From date",
    "logDialog.toDate": "To date (optional)",
    "logDialog.toDateHint": "Leave empty for a single night. Set a later date to log every night of the stay.",
    "logDialog.skipperOverride": "LOG ON BEHALF OF",
    "logDialog.skipperSelf": "Myself (default)",
    "logDialog.skipperOverrideHint": "Attribute this log to another person — e.g. they were on the dock while the boat was under your reservation or in service.",
    "logDialog.multiDaySaved": "Logged {count} nights on the dock",
    "logDialog.linkedReservation": "LINKED RESERVATION (OPTIONAL)",
    "logDialog.tabs.basics": "Basics",
    "logDialog.tabs.conditions": "Conditions",
    "logDialog.tabs.notes": "Notes & reviews",
    "logDialog.tabs.photos": "Photos",
    "logDialog.tabs.fishing": "Fishing",
    "logDialog.place": "Place",
    "logDialog.placePlaceholder": "Bay, marina, restaurant…",
    "logDialog.route": "Route",
    "logDialog.routePlaceholder": "Koper → Piran → Portorož",
    "logDialog.fuel": "Fuel (L)",
    "logDialog.engineHours": "Engine hours",
    "logDialog.weather": "Weather",
    "logDialog.weatherPlaceholder": "Sunny, 24°C, W 8kt",
    "logDialog.seaState": "Sea state",
    "logDialog.seaPlaceholder": "Calm / 1-2ft",
    "logDialog.incidents": "Incidents",
    "logDialog.incidentsPlaceholder": "Anything noteworthy that happened…",
    "logDialog.whatDo": "What to do",
    "logDialog.whatDont": "What NOT to do",
    "logDialog.restaurantReview": "Restaurant review",
    "logDialog.barReview": "Bar review",
    "logDialog.guestExperience": "Guest experience",
    "logDialog.outcome": "OUTCOME",
    "logDialog.outcome.excellent": "excellent",
    "logDialog.outcome.good": "good",
    "logDialog.outcome.poor": "poor",
    "logDialog.outcome.skunked": "skunked",
    "logDialog.species": "Species",
    "logDialog.speciesPlaceholder": "Sea bass, mackerel…",
    "logDialog.method": "Method",
    "logDialog.methodPlaceholder": "Trolling, jigging…",
    "logDialog.bait": "Bait",
    "logDialog.kept": "Kept",
    "logDialog.released": "Released",
    "logDialog.successRating": "SUCCESS RATING",
    "logDialog.saveEntry": "Save entry",
    "logDialog.photosCount": "{count} photo(s)",
    "logDialog.photosDrop": "Click to attach photos (JPEG/PNG/WebP, up to 15MB each)",
    "logDialog.photosHint": "EXIF GPS is extracted automatically. You can override below.",
    "logDialog.tagAllMissing": "Tag all missing with my location",
    "logDialog.caption": "Caption (optional)",
    "logDialog.geoFromExif": "from EXIF",
    "logDialog.geoDevice": "device",
    "logDialog.geoManual": "manual",
    "logDialog.noLocation": "no location",
    "logDialog.useMyLocation": "Use my location",

    // Fishing
    "fishing.title": "Catch memory",
    "fishing.subtitle": "// FISHING INTELLIGENCE",
    "fishing.intro": "Long-term catch data that compounds over seasons — spots, species, method, success.",
    "fishing.stat.trips": "TRIPS",
    "fishing.stat.landed": "FISH LANDED",
    "fishing.stat.avgRating": "AVG RATING",
    "fishing.stat.species": "SPECIES LOGGED",
    "fishing.avgRatingSub": "{excellent} excellent · {good} good",
    "fishing.empty": "No fishing logs yet. Start recording — intelligence grows with every trip.",
    "fishing.speciesTracker": "SPECIES TRACKER",
    "fishing.hotSpots": "HOT SPOTS",
    "fishing.timeline": "TIMELINE",
    "fishing.trips": "trips",
    "fishing.kept": "kept",
    "fishing.released": "released",
    "fishing.visits": "visits",
    "fishing.fish": "fish",

    // Photo Map
    "map.title": "Photo Map",
    "map.subtitle": "GEOTAGGED CAPTURES",
    "map.loading": "Loading pins…",
    "map.status": "{count} photo(s) on the map. Click any thumbnail to open it.",
    "map.loadingTiles": "LOADING TILES…",
    "map.empty": "No geotagged photos yet.",
    "map.emptyHint": "Upload photos from a captain's log with location to see them pinned here.",
    "map.layer.map": "Map",
    "map.layer.satellite": "Satellite",
    "map.layer.terrain": "Terrain",
    "map.layer.nautical": "Nautical",
    "map.sheet.viewJournal": "View journal",
    "map.sheet.captured": "captured",
    "map.sheet.loadFail": "Could not load image.",
    "map.sheet.deletePhoto": "Delete photo",
    "map.sheet.deleteConfirm": "Delete this photo? It will be moved to trash and permanently removed after 30 days.",
    "map.sheet.deleted": "Photo deleted.",

    // Places
    "places.title": "Destinations & hazards",
    "places.subtitle": "PLACE INTELLIGENCE",
    "places.add": "Add place",
    "places.searchLabel": "SEARCH",
    "places.searchPlaceholder": "Name, notes, type…",
    "places.radius": "RADIUS · KM",
    "places.nearMe": "Near me",
    "places.locating": "Locating…",
    "places.emptyNoSearch": "No places yet. Start mapping your routes.",
    "places.emptySearch": "No places match your search.",
    "places.useMyLocation": "Use my current location",
    "places.latitude": "LATITUDE",
    "places.longitude": "LONGITUDE",
    "places.type": "TYPE",
    "places.notes": "NOTES",
    "places.do": "DO",
    "places.dont": "DON'T",
    "places.addPlace": "Add place",
    "places.newReview": "New review",
    "places.addReview": "Add review",
    "places.rating": "RATING",
    "places.comment": "COMMENT",
    "places.reviewType": "TYPE",
    "places.noReviews": "No reviews yet.",
    "places.history": "REVIEW HISTORY · {count} entries",
    "places.reviews": "review(s)",
    "places.distanceAway": "{km} km away",
    "places.types.marina": "Marina",
    "places.types.bay": "Bay",
    "places.types.restaurant": "Restaurant",
    "places.types.bar": "Bar",
    "places.types.fishing_spot": "Fishing spot",
    "places.types.hazard": "Hazard",
    "places.types.excursion_stop": "Excursion stop",
    "places.types.anchorage": "Anchorage",
    "places.types.berth": "Berth",
    "places.reviewTypes.restaurant": "restaurant",
    "places.reviewTypes.bar": "bar",
    "places.reviewTypes.berth": "berth",
    "places.reviewTypes.anchorage": "anchorage",
    "places.reviewTypes.fishing": "fishing",

    // Commercial
    "commercial.title": "Charter & excursions",
    "commercial.subtitle": "COMMERCIAL OPERATIONS",
    "commercial.stat.pending": "PENDING",
    "commercial.stat.approved": "APPROVED",
    "commercial.stat.rejected": "REJECTED",
    "commercial.stat.revenue": "REVENUE",
    "commercial.tab.pending": "Pending approval",
    "commercial.tab.upcoming": "Upcoming",
    "commercial.tab.completed": "Completed",
    "commercial.tab.archive": "Rejected / cancelled",
    "commercial.empty": "No entries in this lane.",
    "commercial.voteHistory": "VOTE HISTORY",
    "commercial.contact": "CONTACT",
    "commercial.notes": "NOTES",
    "commercial.votePlaceholder": "Vote note (audit trail)…",
    "commercial.unnamedClient": "Unnamed client",
    "commercial.votesOf": "{done}/2 votes",
    "commercial.approve": "Approve",
    "commercial.reject": "Reject",
    "commercial.pax": "{count} pax",
    "commercial.noPrice": "no price set",

    // Maintenance
    "maint.title": "Maintenance tickets",
    "maint.subtitle": "SERVICE DESK",
    "maint.new": "New ticket",
    "maint.tab.open": "Open / In progress",
    "maint.tab.done": "Resolved / Closed",
    "maint.tab.all": "All",
    "maint.empty": "No tickets here.",
    "maint.start": "Start",
    "maint.resolve": "Resolve",
    "maint.dialog.title": "New maintenance ticket",
    "maint.dialog.asset": "ASSET",
    "maint.dialog.titleField": "TITLE",
    "maint.dialog.severity": "SEVERITY",
    "maint.dialog.description": "DESCRIPTION",
    "maint.dialog.planned": "Plan scheduled service",
    "maint.dialog.plannedHint": "Toggle on for proactive/future service. Pick a target date.",
    "maint.dialog.scheduledFor": "SCHEDULED FOR",
    "maint.dialog.scheduledEnd": "END DATE (OPTIONAL)",
    "maint.dialog.scheduledEndHint": "Write-up reminder fires on this date",
    "maint.completion.label": "WHAT WAS DONE",
    "maint.dialog.serviceProvider": "SERVICE PROVIDER",
    "maint.plannedBadge": "planned",
    "maint.reactiveBadge": "reactive",
    "maint.tab.planned": "Planned service",
    "maint.tab.reactive": "Reactive",
    "maint.blockAsset": "Block asset period",
    "maint.blockAssetHint": "Create a maintenance reservation that hides the asset from availability for a range.",
    "maint.scheduled": "Scheduled {date}",
    "maint.confirmDelete": "Delete this ticket permanently? Audit trail preserved.",
    "maint.providerEmail": "PROVIDER EMAIL",
    "maint.providerPhone": "PROVIDER PHONE",
    "maint.providerPhoneHint": "E.164 format (+country code). Used for SMS reminders.",
    "maint.autoReminder": "Auto reminders (5 working days + 1 day before)",
    "maint.autoReminderHint": "We'll SMS/email the provider automatically at 06:00 UTC. Both contact fields above must be set for the channel to fire.",
    "maint.sendReminder": "Send reminder",
    "maint.reminderLong": "Long reminder (full details)",
    "maint.reminderShort": "Short reminder (day-before chase)",
    "maint.reminderSent": "Reminder sent · {kind}",
    "maint.reminderFailed": "Reminder failed · see log",
    "maint.reminderPartial": "Reminder partial · see log",
    "maint.autoOn": "auto",
    "maint.lastReminder": "Last reminder {kind} · {date}",
    "maint.noContactHint": "Add a provider email or phone to enable reminders.",
    "maint.reminderChannelSms": "SMS",
    "maint.reminderChannelEmail": "Email",
    "maint.opened": "Opened {date} · {by}",

    // Invoices
    "invoices.title": "Invoices",
    "invoices.subtitle": "ACCOUNTING",
    "invoices.intro": "Upload, categorize, and track what you're paying for.",
    "invoices.new": "Log invoice",
    "invoices.newDialog.title": "Log new invoice",
    "invoices.editDialog.title": "Edit invoice",
    "invoices.stat.total": "TOTAL",
    "invoices.stat.totalSub": "{count} invoice(s)",
    "invoices.stat.outstanding": "OUTSTANDING",
    "invoices.stat.outstandingSub": "Unpaid + overdue",
    "invoices.stat.overdue": "OVERDUE",
    "invoices.stat.overdueSub": "Past due date",
    "invoices.stat.categories": "CATEGORIES",
    "invoices.stat.categoriesSub": "Active tags",
    "invoices.perPayer": "EXPENSES PER PAYER",
    "invoices.paidShort": "paid",
    "invoices.unpaidShort": "unpaid",
    "invoices.unassigned": "Unassigned",
    "invoices.searchPlaceholder": "Vendor, invoice #, description…",
    "invoices.filter.status": "STATUS",
    "invoices.filter.category": "CATEGORY",
    "invoices.filter.paidBy": "PAID BY",
    "invoices.empty": "No invoices yet. Upload your first one.",
    "invoices.col.date": "DATE",
    "invoices.col.vendor": "VENDOR",
    "invoices.col.description": "DESCRIPTION",
    "invoices.col.category": "CATEGORY",
    "invoices.col.paidBy": "PAID BY",
    "invoices.col.amount": "AMOUNT",
    "invoices.col.status": "STATUS",
    "invoices.col.actions": "ACTIONS",
    "invoices.due": "due {date}",
    "invoices.markPaid": "Mark paid",
    "invoices.openAttachment": "Open attachment",
    "invoices.confirmDelete": "Delete this invoice permanently?",
    "invoices.dialog.vendor": "VENDOR *",
    "invoices.dialog.invoiceNum": "INVOICE #",
    "invoices.dialog.amount": "AMOUNT *",
    "invoices.dialog.currency": "CURRENCY",
    "invoices.dialog.date": "DATE *",
    "invoices.dialog.category": "CATEGORY",
    "invoices.dialog.status": "STATUS",
    "invoices.dialog.dueDate": "DUE DATE",
    "invoices.dialog.asset": "ASSET",
    "invoices.dialog.paidBy": "PAID BY (FOR EXPENSE TRACKING)",
    "invoices.dialog.paidByNone": "— not specified —",
    "invoices.dialog.owners": "Owners",
    "invoices.dialog.team": "Team",
    "invoices.dialog.description": "WHAT'S IT ABOUT? *",
    "invoices.dialog.descPlaceholder": "Describe what this invoice is for…",
    "invoices.dialog.attachment": "ATTACHMENT (PDF / IMAGE)",
    "invoices.dialog.attachHint": "Click to attach PDF or image (max 15MB)",
    "invoices.dialog.attachExists": "Attachment on file · click to replace",
    "invoices.dialog.viewExisting": "View existing attachment",
    "invoices.categories.fuel": "fuel",
    "invoices.categories.maintenance": "maintenance",
    "invoices.categories.insurance": "insurance",
    "invoices.categories.dockage": "dockage",
    "invoices.categories.provisions": "provisions",
    "invoices.categories.supplies": "supplies",
    "invoices.categories.services": "services",
    "invoices.categories.commercial": "commercial",
    "invoices.categories.other": "other",

    // Documents
    "documents.title": "Documents & expiry",
    "documents.subtitle": "COMPLIANCE",
    "documents.new": "Upload document",
    "documents.pageNew": "NEW",
    "documents.empty": "No documents yet. Upload licenses, insurance, registrations.",
    "documents.col.title": "TITLE",
    "documents.col.type": "TYPE",
    "documents.col.asset": "ASSET",
    "documents.col.expiry": "EXPIRY",
    "documents.col.uploaded": "UPLOADED",
    "documents.dialog.title": "Upload document",
    "documents.dialog.editTitle": "Edit document",
    "documents.dialog.titleField": "TITLE",
    "documents.dialog.titleRequired": "Title is required",
    "documents.dialog.type": "TYPE",
    "documents.dialog.asset": "ASSET",
    "documents.dialog.expiry": "EXPIRY DATE",
    "documents.dialog.file": "FILE (OPTIONAL)",
    "documents.dialog.pages": "PAGES",
    "documents.dialog.addPage": "Add page",
    "documents.dialog.dropHint": "Drag & drop files here, or click to choose. PDF + photos welcome.",
    "documents.dialog.acceptTypes": "PDF · JPG · PNG · HEIC · HEIF",
    "documents.dialog.reorderHint": "Drag pages by the ⋮⋮ handle to reorder.",
    "documents.dialog.dragToReorder": "Drag to reorder",
    "documents.pages": "pages",
    "documents.pageNum": "Page {n}",
    "documents.dragHint": "Tip — drop files anywhere on this page to start a new document, or click ✏️ on an existing row to add more pages.",
    "documents.savedToast": "Document saved",
    "documents.updatedToast": "Document updated",
    "documents.pageDrop.title": "Drop files to start a new document",
    "documents.pageDrop.hint": "Mixed PDFs and photos get stitched into a single multi-page document.",
    "documents.noFile": "This document has no pages yet.",
    "documents.emailDocument": "Email this document",
    "documents.email.title": "Email document",
    "documents.email.summary": "ATTACHMENT",
    "documents.email.toLabel": "TO EMAIL",
    "documents.email.subjectLabel": "SUBJECT",
    "documents.email.messageLabel": "MESSAGE (OPTIONAL)",
    "documents.email.messagePlaceholder": "Hi, please find the registration attached…",
    "documents.email.send": "Send",
    "documents.email.sending": "Sending…",
    "documents.email.sentToast": "Document emailed",
    "documents.email.invalidEmail": "Please enter a valid email address",

    // Damage Reports
    "damage.subtitle": "// DAMAGE TRACKING",
    "damage.title": "Damage reports",
    "damage.intro": "Capture damage with photos + a description, then turn it into a repair email and/or a maintenance ticket — all from one place.",
    "damage.new": "New damage report",
    "damage.empty": "No damage reports yet — fingers crossed.",
    "damage.deleteConfirm": "Delete this damage report? It will be moved to trash and auto-purged in 30 days.",
    "damage.deletedToast": "Damage report deleted",
    "damage.savedToast": "Damage report saved",
    "damage.updatedToast": "Damage report updated",
    "damage.linkedTicket": "Linked maintenance ticket:",
    "damage.orderRepair": "Order repair",
    "damage.filter.asset": "Asset",
    "damage.filter.status": "Status",
    "damage.severity.low": "Low",
    "damage.severity.medium": "Medium",
    "damage.severity.high": "High",
    "damage.severity.critical": "Critical",
    "damage.status.open": "Open",
    "damage.status.quoted": "Quoted",
    "damage.status.in_progress": "In progress",
    "damage.status.repaired": "Repaired",
    "damage.status.closed": "Closed",
    "damage.dialog.title": "New damage report",
    "damage.dialog.editTitle": "Edit damage report",
    "damage.dialog.assetLabel": "ASSET",
    "damage.dialog.assetRequired": "Pick an asset first",
    "damage.dialog.titleLabel": "WHAT HAPPENED",
    "damage.dialog.titlePlaceholder": "Crack on starboard hull near waterline",
    "damage.dialog.titleRequired": "Give it a short title",
    "damage.dialog.severityLabel": "SEVERITY",
    "damage.dialog.location": "Location on asset",
    "damage.dialog.locationPlaceholder": "Starboard hull, near aft, ~30cm below waterline",
    "damage.dialog.dateObserved": "Date observed",
    "damage.dialog.description": "DESCRIPTION",
    "damage.dialog.descriptionPlaceholder": "Describe what you saw, when it happened (if you know), and any immediate concerns.",
    "damage.dialog.descRequired": "A short description is required",
    "damage.dialog.photos": "PHOTOS",
    "damage.dialog.photoCount": "photo(s)",
    "damage.dialog.photosRequired": "At least one photo is required",
    "damage.dialog.addPhoto": "Add photo",
    "damage.dialog.dropHint": "Drop photos here or click to pick. Camera-roll & HEIC welcome.",
    "damage.order.title": "Order repair",
    "damage.order.emailHeader": "EMAIL A WORKSHOP",
    "damage.order.ticketHeader": "ALSO CREATE A MAINTENANCE TICKET",
    "damage.order.scheduledFor": "SCHEDULED FOR (OPTIONAL)",
    "damage.order.providerPhone": "WORKSHOP PHONE (FOR SMS REMINDERS)",
    "damage.order.messagePlaceholder": "Hi, could you give us a quote and your earliest availability for the attached repair?",
    "damage.order.submit": "Send",
    "damage.order.nothingToDo": "Add an email or check 'create ticket' — at least one action is required",
    "damage.order.toastEmailed": "emailed",
    "damage.order.toastTicket": "ticket created",
    "damage.order.toastDone": "Done",

    // Accessibility (text size)
    "a11y.cycle": "Text size",
    "a11y.size.normal": "Normal",
    "a11y.size.large": "Large text",
    "a11y.size.xl": "Extra-large text",

    // Missing logs dashboard quick-action
    "dashboard.missingLogs.title": "Past trips without a log",
    "dashboard.missingLogs.intro": "These approved reservations don't have a captain's log yet. Tap one to log it now.",
    "dashboard.missingLogs.empty": "All caught up — every past trip is logged. 🙌",
    "dashboard.missingLogs.logBtn": "Log this trip",
    "dashboard.missingLogs.viewAll": "View all",
    "dashboard.missingLogs.daysAgo": "{n}d ago",
    "dashboard.missingLogs.clickHint": "Click to see all and log them",

    // Reports
    "reports.title": "Operational reports",
    "reports.email.button": "Email report to owners",
    "reports.email.sending": "Sending…",
    "reports.email.sent": "Report emailed to {emails}",
    "reports.email.partial": "Partially sent — delivered to {emails}",
    "reports.email.failed": "Could not send the report email",
    "reports.season": "SEASON {year}",
    "reports.seasonal.heading": "SEASONAL ALLOCATION",
    "reports.seasonal.sub": "Owner private use · {start} → {end}",
    "reports.seasonal.metDays": "14-day met",
    "reports.seasonal.belowDays": "below 14-day",
    "reports.seasonal.days": "DAYS",
    "reports.seasonal.weekends": "WEEKENDS",
    "reports.seasonal.longest": "LONGEST",
    "reports.revenue.heading": "COMMERCIAL REVENUE",
    "reports.revenue.count": "{count} completed / approved trips",
    "reports.revenue.none": "No commercial revenue yet.",
    "reports.missing.heading": "MISSING LOGS",
    "reports.missing.sub": "Past reservations without a captain's log",
    "reports.audit.heading": "AUDIT TIMELINE",
    "reports.audit.limited": "Audit view limited or empty. Sign in as director / admin to see full trail.",

    // Settings
    "settings.title": "Settings",
    "settings.subtitle": "CONFIGURATION",
    "settings.agreement": "AGREEMENT PARAMETERS",
    "settings.param.split": "Ownership split",
    "settings.param.splitValue": "Rok Kobal 50% · Borut Krzic 50%",
    "settings.param.season": "Summer season",
    "settings.param.seasonValue": "01 Jun → 30 Sep",
    "settings.param.protected": "Protected term minimum",
    "settings.param.protectedValue": "14 consecutive days per owner",
    "settings.param.approval": "Commercial approval",
    "settings.param.approvalValue": "Dual director (both must approve)",
    "settings.param.bareboat": "Bareboat rental",
    "settings.param.bareboatValue": "NOT PERMITTED",
    "settings.param.excursion": "Excursion skipper",
    "settings.param.excursionValue": "Required · fuel billable to client",
    "settings.param.deciding": "Deciding vote",
    "settings.param.decidingValue": "Rok Kobal — cannot override protected terms",
    "settings.param.holiday": "May 1 holiday",
    "settings.param.holidayValue": "Rotates yearly between owners",
    "settings.team": "TEAM ({count})",
    "settings.col.name": "NAME",
    "settings.col.email": "EMAIL",
    "settings.col.role": "ROLE",
    "settings.col.status": "STATUS",
    "settings.active": "active",
    "settings.inactive": "inactive",

    // Manual page
    "manual.subtitle": "// USER MANUAL",
    "manual.title": "How LaSovita Ledger works",
    "manual.intro": "Roles, flows, rules, intelligence math, notifications and troubleshooting — single source of truth for everyone using the platform.",
    "manual.downloadPdf": "Download PDF",
    "manual.emailMe": "Email me a copy",
    "manual.emailSent": "Manual emailed",
  },

  sl: {
    "lang.name": "Slovenščina",
    "lang.short": "SL",

    "nav.dashboard": "Nadzorna plošča",
    "nav.calendar": "Koledar",
    "nav.assets": "Sredstva",
    "nav.journal": "Dnevnik",
    "nav.damage": "Poškodbe",
    "nav.fishing": "Ribolov",
    "nav.map": "Foto zemljevid",
    "nav.weather": "Vreme",
    "nav.places": "Kraji",
    "nav.commercial": "Komercialno",
    "nav.maintenance": "Vzdrževanje",
    "nav.invoices": "Računi",
    "nav.documents": "Dokumenti",
    "nav.reports": "Poročila",
    "nav.intelligence": "Analitika",
    "nav.trash": "Koš",
    "nav.manual": "Priročnik",
    "nav.settings": "Nastavitve",

    "header.subtitle": "OPERATIVNA KNJIGA RESNICE",
    "header.home": "Domov",

    "common.signOut": "Odjava",
    "common.save": "Shrani",
    "common.saving": "Shranjujem…",
    "common.saveChanges": "Shrani spremembe",
    "common.cancel": "Prekliči",
    "common.edit": "Uredi",
    "common.delete": "Izbriši",
    "common.keep": "Obdrži",
    "common.add": "Dodaj",
    "common.new": "Novo",
    "common.search": "Iskanje",
    "common.apply": "Uporabi",
    "common.loading": "Nalagam…",
    "common.all": "Vse",
    "common.none": "— brez —",
    "common.noneLabel": "ni določeno",
    "common.details": "Podrobnosti",
    "common.hide": "Skrij",
    "common.optional": "neobvezno",
    "common.required": "obvezno",
    "common.notes": "Opombe",
    "common.description": "Opis",
    "common.actions": "Dejanja",
    "common.type": "Tip",
    "common.status": "Stanje",
    "common.date": "Datum",
    "common.amount": "Znesek",
    "common.vendor": "Dobavitelj",
    "common.category": "Kategorija",
    "common.name": "Ime",
    "common.owner": "Lastnik",
    "common.client": "Stranka",
    "common.from": "od",
    "common.to": "do",
    "common.yes": "Da",
    "common.no": "Ne",

    "role.super_admin": "Skrbnik sistema",
    "role.owner_director": "Lastnik / Direktor",
    "role.skipper": "Skiper",
    "role.accountant": "Računovodja",
    "role.operations_admin": "Operativni skrbnik",
    "role.maintenance_user": "Vzdrževanje",

    "status.approved": "odobreno",
    "status.pending_approval": "čaka odobritev",
    "status.rejected": "zavrnjeno",
    "status.cancelled": "preklicano",
    "status.draft": "osnutek",
    "status.protected": "ZAŠČITENO",
    "status.open": "odprto",
    "status.in_progress": "v teku",
    "status.resolved": "rešeno",
    "status.closed": "zaprto",
    "status.paid": "plačano",
    "status.unpaid": "neplačano",
    "status.overdue": "zapadlo",
    "status.available": "na voljo",
    "status.reserved": "rezervirano",
    "status.maintenance": "vzdrževanje",
    "status.out_of_service": "izven uporabe",

    "reservation.private": "Zasebno",
    "reservation.charter_skippered": "Čarter (s skiperjem)",
    "reservation.excursion": "Izlet",
    "reservation.maintenance_block": "Blok za vzdrževanje",

    "severity.low": "nizka",
    "severity.medium": "srednja",
    "severity.high": "visoka",
    "severity.critical": "kritična",

    "login.restricted": "// OMEJEN DOSTOP",
    "login.signInToContinue": "Prijavite se za nadaljevanje",
    "login.authorisedOnly": "Interno operativno orodje. Samo za pooblaščeno osebje.",
    "login.email": "E-POŠTA",
    "login.password": "GESLO",
    "login.submit": "PRIJAVA",
    "login.authenticating": "PREVERJANJE…",
    "login.testAccounts": "TESTNI RAČUNI · GESLO: LaSovita2026!",
    "login.createAccount": "Ustvari račun",
    "login.signInTab": "Prijava",
    "login.signUpTab": "Prošnja za dostop",
    "login.signUpTitle": "Prošnja za dostop do LaSovita Ledger",
    "login.signUpSub": "Administrator bo pregledal vašo prošnjo. Prijava bo mogoča šele po odobritvi — brez samodejnega dostopa.",
    "login.fullName": "CELO IME",
    "login.phone": "TELEFON (NEOBVEZNO)",
    "login.passwordMin": "Najmanj 8 znakov",
    "login.submitSignUp": "Pošlji prošnjo",
    "login.submittingSignUp": "Pošiljanje…",
    "login.signUpSuccess": "Prošnja je bila poslana. Administrator jo bo kmalu pregledal — po odobritvi se boste lahko prijavili.",
    "login.backToSignIn": "← Nazaj na prijavo",
    "login.awaitingApproval": "Vaš račun čaka na odobritev administratorja.",
    "login.forgotLink": "Pozabljeno geslo?",
    "login.forgotLabel": "// PONASTAVITEV GESLA",
    "login.forgotTitle": "Ponastavi geslo",
    "login.forgotSub": "Vnesite e-poštni naslov računa. Če ustreza aktivnemu računu, vam pošljemo povezavo za ponastavitev gesla. Povezava poteče v 30 minutah.",
    "login.forgotSubmit": "Pošlji povezavo",
    "login.forgotSubmitting": "Pošiljanje…",
    "login.forgotSuccessTitle": "Preverite e-pošto",
    "login.forgotSuccessBody": "Če za vneseni e-poštni naslov obstaja račun, smo poslali povezavo za ponastavitev. Povezava poteče v 30 minutah.",
    "reset.label": "// NASTAVITE NOVO GESLO",
    "reset.title": "Nastavite novo geslo",
    "reset.subtitle": "Izberite novo geslo za vaš LaSovita Ledger račun.",
    "reset.newPassword": "NOVO GESLO",
    "reset.confirmPassword": "POTRDITE NOVO GESLO",
    "reset.submit": "Posodobi geslo",
    "reset.submitting": "Shranjevanje…",
    "reset.tooShort": "Geslo mora imeti vsaj 8 znakov.",
    "reset.mismatch": "Gesli se ne ujemata.",
    "reset.doneTitle": "Geslo posodobljeno",
    "reset.doneBody": "Vaše geslo je bilo spremenjeno. Sedaj se lahko prijavite z novim geslom.",
    "reset.signInNow": "Prijava",
    "reset.missingTokenTitle": "Manjkajoča ali neveljavna povezava",
    "reset.missingTokenBody": "Ta povezava za ponastavitev nima žetona. Prosimo, zahtevajte novo s strani za prijavo.",
    "nav.approvals": "Odobritve",
    "approvals.subtitle": "NADZOR DOSTOPA",
    "approvals.title": "Čakajoče prošnje za dostop",
    "approvals.intro": "Preglejte uporabnike, ki so zaprosili za dostop. Odobri z izbiro vloge in omogoči prijavo, ali zavrni.",
    "approvals.empty": "Ni čakajočih prošenj.",
    "approvals.requested": "Prošnja {date}",
    "approvals.role": "VLOGA OB ODOBRITVI",
    "approvals.approve": "Odobri",
    "approvals.reject": "Zavrni",
    "approvals.confirmReject": "Zavrnem to prošnjo za dostop? Uporabnik se ne bo mogel prijaviti.",
    "approvals.approved": "Dostop odobren · {name}",
    "approvals.rejected": "Prošnja zavrnjena",
    "approvals.pendingBadge": "čaka",
    "login.tagline1": "Vsaka rezervacija.",
    "login.tagline2": "Vsak dnevnik.",
    "login.tagline3": "Vsaka odobritev.",
    "login.tagline4": "Zabeležena.",
    "login.description": "Zasebna operativna platforma za skupno upravljano čolnarjenje. Narejena za odločitve na pomolu, revizijske sledi in desetletja znanja o krajih.",
    "login.operationalSystem": "OPERATIVNI SISTEM RESNICE",

    // Media gallery (generic)
    "gallery.photos": "FOTOGRAFIJE",
    "gallery.add": "Dodaj fotografije",
    "gallery.uploading": "Nalaganje…",
    "gallery.uploaded": "Naloženih {count} fotografij",
    "gallery.uploadFailed": "{count} fotografij ni bilo mogoče naložiti",
    "gallery.deleted": "Fotografija izbrisana",
    "gallery.empty": "Še ni fotografij.",
    "gallery.captured": "posneto",
    "gallery.takePhoto": "Fotografiraj",

    // Holiday rotation
    "holidays.subtitle": "ROTACIJA PRAZNIKOV",
    "holidays.intro": "Samodejno generirana rotacija državnih praznikov in ključnih datumov. Rotacija se vsako leto zamenja, da oba lastnika dobita pravičen delež. Administratorji lahko spodaj spremenijo datume in dodeljenega lastnika.",
    "holidays.saved": "Praznik shranjen",
    "holidays.empty": "Prazniki niso nastavljeni — najprej dodajte vsaj dva lastnika-direktorja.",
    "holidays.names.new_year": "Novo leto",
    "holidays.names.preseren_day": "Prešernov dan",
    "holidays.names.easter_monday": "Velikonočni ponedeljek",
    "holidays.names.may_1": "Praznik dela (1.–2. maj)",
    "holidays.names.statehood_day": "Dan državnosti",
    "holidays.names.assumption_day": "Marijino vnebovzetje",
    "holidays.names.reformation_day": "Dan reformacije",
    "holidays.names.all_saints": "Dan spomina na mrtve",
    "holidays.names.christmas": "Božič in dan samostojnosti",

    // Intelligence
    "intel.subtitle": "// ANALITIČNI SLOJ",
    "intel.title": "Operativna inteligenca",
    "intel.intro": "Združen vpogled v ribiško uspešnost, ritem vzdrževanja in priložnosti za izkoriščenost — posodobljeno v živo z vnosi dnevnikov in rezervacij.",
    "intel.tab.heatmap": "RIBIŠKA TOPLOTNA KARTA",
    "intel.tab.forecast": "NAPOVED VZDRŽEVANJA",
    "intel.tab.utilization": "IZKORIŠČENOST",
    "intel.tab.usage": "UPORABA",
    "intel.usage.formula": "Uporaba = dnevi z vpisom v dnevnik · rezervacije in vzdrževalni dnevi se ne štejejo, razen če zabeležite »prenočitev na privezu«",
    "intel.usage.empty": "Letos še ni ustreznih vnosov v dnevnik.",
    "intel.usage.days": "dni",
    "intel.usage.ofTotal": "od pripisanega",
    "intel.usage.dockNights": "noči na privezu",
    "intel.usage.monthly": "MESEČNA UPORABA — DNI",
    "intel.usage.distinctTotal": "ŠTEVILO DNI UPORABE FLOTE",
    "intel.usage.bothSameDay": "DNI Z ≥2 LASTNIKOMA",
    "intel.usage.sharedNudge": "skupnih dni z ≥2 lastnikoma na krovu — kredit je razdeljen 50/50, da ostanejo skupni rezultati pošteni, ko ste pluli skupaj.",
    "intel.usage.sharedNudgeOne": "skupni dan z ≥2 lastnikoma na krovu — kredit je razdeljen 50/50, da ostanejo skupni rezultati pošteni, ko ste pluli skupaj.",
    "intel.heatmap.empty": "Ni geolociranih ribiških dnevnikov. Ustvarite ribiški dnevnik z zajeto lokacijo, da vidite vroče točke.",
    "intel.heatmap.trips": "izhodov",
    "intel.heatmap.avgRating": "povprečna ocena",
    "intel.heatmap.topSpecies": "NAJPOGOSTEJŠE VRSTE",
    "intel.heatmap.latest": "Zadnji",
    "intel.forecast.empty": "Še ni sredstev z zgodovino vzdrževanja.",
    "intel.forecast.lastService": "ZADNJI SERVIS",
    "intel.forecast.daysSince": "DNI OD",
    "intel.forecast.avgInterval": "POV. INTERVAL",
    "intel.forecast.predictedNext": "NAPOVEDANO",
    "intel.forecast.tickets12mo": "SERVISI (12m)",
    "intel.forecast.cost12mo": "STROŠEK (12m)",
    "intel.forecast.ticketsTotal": "SKUPAJ",
    "intel.forecast.overdue": "zapadlo",
    "intel.forecast.onTrack": "po načrtu",
    "intel.forecast.noHistory": "ni zgodovine",
    "intel.utilization.title": "PREGLED LETA",
    "intel.utilization.daysReserved": "d rezerviranih",
    "intel.utilization.private": "Zasebno",
    "intel.utilization.charter": "Najem",
    "intel.utilization.maintenance": "Vzdrževanje",
    "intel.utilization.idleTitle": "NEIZKORIŠČENI VIKENDI V GLAVNI SEZONI",
    "intel.utilization.idleIntro": "Sobote in nedelje od junija do septembra brez rezervacije. To so priložnosti za prihodek, ki jih lahko zapolnite z najemom, izleti ali zasebno uporabo.",
    "intel.utilization.noIdle": "Vsi vikendi v glavni sezoni so rezervirani. Odlično.",
    "intel.utilization.moreIdle": "več",
    "common.loading": "Nalaganje…",

    // Maintenance reminder language field
    "maintenance.reminderLanguage": "JEZIK OPOMNIKA",
    "maintenance.reminderLanguage.en": "Angleščina",
    "maintenance.reminderLanguage.sl": "Slovenščina",

    // Weather (SL)
    "weather.section": "RAZMERE NA DOMAČI BAZI",
    "weather.title": "Vreme · Portorož",
    "weather.loading": "Prenašam trenutne razmere…",
    "weather.loadFail": "Vremenska storitev ni dosegljiva.",
    "weather.updated": "Posodobljeno",
    "weather.temp": "TEMP. ZRAKA",
    "weather.feelsLike": "Občuteno",
    "weather.wind": "VETER",
    "weather.gust": "sunki",
    "weather.wave": "VALOVI",
    "weather.seaTemp": "TEMP. MORJA",
    "weather.cloud": "OBLAČNOST",
    "weather.precip": "PADAVINE",
    "weather.pressure": "TLAK",
    "weather.humidity": "VLAŽNOST",
    "weather.sun": "SONCE",
    "weather.sunrise": "Sončni vzhod",
    "weather.sunset": "Sončni zahod",
    "weather.uvMax": "Najvišji UV indeks danes",
    "weather.moon": "LUNA",
    "weather.moonHint": "Okvirna faza — uporabno za ribolov in nočno plovbo.",
    "weather.phase.new_moon": "Mlaj",
    "weather.phase.waxing_crescent": "Naraščajoči krajec",
    "weather.phase.first_quarter": "Prvi krajec",
    "weather.phase.waxing_gibbous": "Naraščajoča luna",
    "weather.phase.full_moon": "Polna luna",
    "weather.phase.waning_gibbous": "Pojemajoča luna",
    "weather.phase.last_quarter": "Zadnji krajec",
    "weather.phase.waning_crescent": "Pojemajoči krajec",
    "weather.tides": "PLIMA / OSEKA",
    "weather.tidesNone": "Ni plimskih dogodkov v naslednjih 3 dneh.",
    "weather.tide.high": "Plima",
    "weather.tide.low": "Oseka",
    "weather.forecast7d": "NAPOVED 7 DNI",


    // Trash (unified)
    "trash.subtitle": "KOŠ ZA SMETI",
    "trash.title": "Koš",
    "trash.intro": "Izbrisani dnevniki, vzdrževalne prijave in računi se hranijo tu 30 dni. Lahko jih obnovite kadar koli — po 30 dneh se samodejno trajno izbrišejo.",
    "trash.empty": "Koš je prazen. Odlično.",
    "trash.section.logs": "IZBRISANI DNEVNIKI",
    "trash.section.tickets": "IZBRISANE VZDRŽEVALNE PRIJAVE",
    "trash.section.invoices": "IZBRISANI RAČUNI",
    "trash.restore": "Obnovi",
    "trash.restoredToast": "Obnovljeno",
    "trash.purgedToast": "Trajno izbrisano",
    "trash.purgeConfirm": "TRAJNO izbrišem ta vnos ZDAJ? To dejanje je nepovratno.",
    "trash.purgeNow": "Izbriši zdaj",
    "trash.purgesOn": "ČISTKA",

    // Invoice AI scan
    "invoices.scan.label": "// AI ODČITAJ RAČUN",
    "invoices.scan.help": "Naložite fotografijo ali PDF računa in samodejno bomo izpolnili dobavitelja, datum, znesek, DDV in kategorijo.",
    "invoices.scan.btn": "Skeniraj račun",
    "invoices.scan.scanning": "Branje…",
    "invoices.scanSuccess": "Račun prepoznan — preverite pred shranjevanjem",

    // Documents view + delete
    "documents.view": "Ogled",
    "documents.deleteConfirm": "Izbrišem ta dokument? Dejanje je trajno.",
    "documents.deletedToast": "Dokument izbrisan",
    "documents.noFile": "Ta dokument nima priložene datoteke",

    // Places edit + delete
    "places.editPlace": "Uredi mesto",
    "places.updatedToast": "Mesto posodobljeno",
    "places.addedToast": "Mesto dodano",
    "places.deleteConfirm": "Izbrišem \"{name}\"? Izbrisane bodo tudi vse ocene. Dejanje je trajno.",
    "places.deletedToast": "Mesto izbrisano",

    "dashboard.welcome": "// DOBRODOŠLI",
    "dashboard.welcomeBack": "Lepo te je videti,",
    "dashboard.statusLoading": "Nalagam stanje flote…",
    "dashboard.statusLine": "{assets} sredstev · {logs} dnevnikov · {pending} odobritev v čakanju",
    "dashboard.newReservation": "Nova rezervacija",
    "dashboard.logTrip": "Zabeleži plovbo",
    "dashboard.snapReceipt": "Posnemi račun",
    "dashboard.fleet": "FLOTA",
    "dashboard.fleetSub": "Aktivna sredstva",
    "dashboard.awaitingVote": "ČAKA GLAS",
    "dashboard.awaitingVoteSub": "Komercialne odobritve",
    "dashboard.openTickets": "ODPRTI TIKETI",
    "dashboard.openTicketsSub": "Vzdrževalna vrsta",
    "dashboard.missingLogs": "MANJKAJOČI DNEVNIKI",
    "dashboard.missingLogsSub": "Pretekle plovbe brez dnevnika",
    "dashboard.upcomingLabel": "PRIHAJAJOČE",
    "dashboard.upcomingTitle": "Naslednje rezervacije",
    "dashboard.viewCalendar": "Odpri koledar",
    "dashboard.noUpcoming": "Ni prihajajočih rezervacij. Koledar je prazen.",
    "dashboard.dualApproval": "DVOJNA ODOBRITEV",
    "dashboard.awaitingDirectors": "Čaka direktorja",
    "dashboard.noPending": "Ni čakajočih odobritev. Prosto.",
    "dashboard.recentActivity": "NEDAVNA AKTIVNOST",
    "dashboard.captainsJournal": "Kapitanov dnevnik",
    "dashboard.usage.label": "URAVNOTEŽENOST UPORABE",
    "dashboard.usage.title": "Rok proti Borutu — {year}",
    "dashboard.usage.subtitle": "Kdo je doslej v tej sezoni uporabljal čoln — na prvi pogled.",
    "dashboard.usage.nights": "Noči na krovu",
    "dashboard.usage.trips": "Dnevni izleti",
    "dashboard.usage.credited": "Pripisani dnevi",
    "dashboard.usage.shared": "{n} skupnih dni · razdeljeno 50/50",
    "dashboard.usage.sharedOne": "{n} skupni dan · razdeljeno 50/50",
    "dashboard.usage.even": "Popolnoma izenačeno",
    "dashboard.usage.ahead": "{name} v prednosti za {n}",
    "dashboard.usage.empty": "V tej sezoni še ni zabeleženih dni.",
    "dashboard.usage.viewMore": "Celoten pregled",
    "dashboard.writeup.label": "ZAKLJUČEK VZDRŽEVANJA",
    "dashboard.writeup.title": "Zabeleži, kaj je bilo opravljeno",
    "dashboard.writeup.intro": "Ta načrtovana dela so se zaključila. Zapišite, kaj je bilo dejansko opravljeno, da bo zapis popoln.",
    "dashboard.writeup.endedAgo": "končano pred {n} dni",
    "dashboard.writeup.recordBtn": "Zabeleži",
    "dashboard.writeup.dialogTitle": "Zapis o vzdrževanju",
    "dashboard.writeup.notesLabel": "KAJ JE BILO OPRAVLJENO",
    "dashboard.writeup.notesPlaceholder": "npr. dvig čolna 28/05, menjava cinkov, poliranje, protiobraščanje, globinomer (oddajnik + kabel), nazaj v vodo 01/06",
    "dashboard.writeup.notesRequired": "Prosimo, zapišite, kaj je bilo opravljeno",
    "dashboard.writeup.photosLabel": "FOTOGRAFIJE OPRAVLJENEGA DELA (NEOBVEZNO)",
    "dashboard.writeup.photosDrop": "Dodaj fotografije opravljenega dela",
    "dashboard.writeup.saveBtn": "Shrani zapis",
    "dashboard.writeup.savedToast": "Zapis o vzdrževanju shranjen",
    "dashboard.openJournal": "Odpri dnevnik",
    "dashboard.noLogs": "Še ni vnosov. Zabeležite prvo plovbo.",
    "dashboard.votes": "glasov",

    "calendar.today": "DANES",
    "calendar.now": "ZDAJ",
    "calendar.wx.clear": "Jasno",
    "calendar.wx.partly": "Delno oblačno",
    "calendar.wx.cloudy": "Oblačno",
    "calendar.wx.fog": "Megla",
    "calendar.wx.rain": "Dež",
    "calendar.wx.snow": "Sneg",
    "calendar.wx.storm": "Nevihta",
    "calendar.wx.wind": "veter",
    "calendar.prevMonth": "Prejšnji mesec",
    "calendar.nextMonth": "Naslednji mesec",
    "calendar.allAssets": "Vsa sredstva",
    "calendar.newReservation": "Nova rezervacija",
    "calendar.more": "več",
    "calendar.agendaTitle": "Agenda meseca",
    "calendar.agendaDays": "dni z rezervacijami",
    "calendar.agendaEmpty": "V tem mesecu ni rezervacij.",
    "calendar.protected": "zaščiteno",
    "calendar.add": "Nova",
    "calendar.viewAgenda": "AGENDA",
    "calendar.viewGrid": "MREŽA",
    "calendar.legend.pending": "Čaka odobritev",
    "calendar.legend.approved": "Odobreno komercialno",
    "calendar.legend.maintenance": "Vzdrževanje",
    "calendar.legend.notUsed": "Neuporabljeno (void)",
    "calendar.legend.dayTrip": "Dnevni izlet (dnevnik)",
    "calendar.legend.onDock": "Prenočitev na privezu",
    "calendar.logsOn": "Dnevniki: vključeni",
    "calendar.logsOff": "Dnevniki: izključeni",
    "calendar.logsToggleHint": "Pokaži / skrij dnevnike na koledarju",
    "calendar.void": "VOID",
    "calendar.legend.ownerPrivate": "{name} · zasebno",
    "calendar.legend.ownerProtected": "{name} · zaščiteno",
    "calendar.days.mon": "PON", "calendar.days.tue": "TOR", "calendar.days.wed": "SRE",
    "calendar.days.thu": "ČET", "calendar.days.fri": "PET", "calendar.days.sat": "SOB", "calendar.days.sun": "NED",
    "calendar.detail.asset": "Sredstvo",
    "calendar.detail.dates": "Termin",
    "calendar.detail.client": "Stranka",
    "calendar.detail.passengers": "Potniki",
    "calendar.detail.price": "Cena plovbe",
    "calendar.detail.requestedBy": "Zaprosil",
    "calendar.detail.approvals": "ODOBRITVE",
    "calendar.detail.yourVote": "// VAŠ GLAS JE POTREBEN",
    "calendar.detail.approve": "Odobri",
    "calendar.detail.reject": "Zavrni",
    "calendar.detail.notePlaceholder": "Neobvezna opomba (v revizijski sledi)…",
    "calendar.detail.confirmDelete": "Trajno izbrišem to rezervacijo? Tega ni mogoče razveljaviti — zapis izgine iz koledarja, a ostane v revizijski sledi.",
    "calendar.detail.markNotUsed": "Označi kot neuporabljeno",
    "calendar.detail.markUsed": "Označi kot uporabljeno",
    "calendar.detail.notUsedPrompt": "Zabeleži, da čoln v tej rezervaciji ni bil uporabljen. Ostane na koledarju (in še naprej zaseda termin), a je prikazan kot neuporabljen.",
    "calendar.detail.notUsedReasonPlaceholder": "Razlog (neobvezno) — npr. slabo vreme",
    "calendar.detail.confirmNotUsed": "Označi kot neuporabljeno",
    "calendar.detail.markedNotUsed": "Rezervacija označena kot neuporabljena",
    "calendar.detail.markedUsed": "Rezervacija označena kot uporabljena",
    "calendar.detail.noReason": "Razlog ni naveden",
    "calendar.detail.yesDelete": "Da, izbriši",
    "calendar.detail.linkedLogs": "PRIPETI DNEVNIKI",
    "calendar.detail.noLinkedLogs": "K tej rezervaciji še ni priložen noben dnevnik.",
    "calendar.detail.addLog": "Dodaj dnevnik",
    "calendar.detail.history": "ZGODOVINA SPREMEMB",
    "calendar.detail.noHistory": "Ni revizijskih dogodkov.",
    "calendar.detail.confirmDeleteLog": "Trajno izbrišem ta dnevnik? Zapis se odstrani iz dnevnika, a ostane v revizijski sledi.",
    "audit.action.create": "ustvarjeno",
    "audit.action.update": "urejeno",
    "audit.action.delete": "izbrisano",
    "audit.action.vote_approved": "odobreno",
    "audit.action.vote_rejected": "zavrnjeno",
    "audit.changed": "Spremenjeno",

    "resDialog.new": "Nova rezervacija",
    "resDialog.edit": "Uredi rezervacijo",
    "resDialog.type": "VRSTA",
    "resDialog.asset": "Sredstvo",
    "resDialog.startDate": "Začetek",
    "resDialog.endDate": "Konec",
    "resDialog.duration": "Trajanje: {days} dni",
    "resDialog.owner": "Lastnik",
    "resDialog.selectOwner": "Izberi lastnika",
    "resDialog.markProtected": "Označi kot zaščiten termin",
    "resDialog.protectedHint": "Komercialne zahteve ga ne morejo prevzeti (priporočeno ≥14 zaporednih dni)",
    "resDialog.fourteenWarning": "Zaščiteni termini so po pogodbi lastnikov običajno 14+ zaporednih dni.",
    "resDialog.clientName": "Ime stranke",
    "resDialog.clientContact": "Kontakt stranke",
    "resDialog.passengers": "Potniki",
    "resDialog.priceEur": "Cena plovbe (€)",
    "resDialog.commercialWarning": "Komercialne rezervacije morata pred potrditvijo odobriti oba direktorja.",
    "resDialog.notesPlaceholder": "Neobvezen kontekst za to rezervacijo…",
    "resDialog.conflicts": "ZAZNANI KONFLIKTI ({count})",
    "resDialog.savedReservation": "Shrani rezervacijo",

    "assets.title": "Sredstva",
    "assets.subtitle": "REGISTER FLOTE",
    "assets.add": "Dodaj sredstvo",
    "assets.edit": "Uredi sredstvo",
    "assets.new": "Dodaj sredstvo",
    "assets.none": "Ni sredstev. Dodajte prvo plovilo.",
    "assets.types.boat": "čoln",
    "assets.types.jetski": "jet ski",
    "assets.types.trailer": "prikolica",
    "assets.fields.type": "VRSTA",
    "assets.fields.status": "STANJE",
    "assets.fields.reg": "REGISTRACIJA",
    "assets.fields.serial": "SERIJSKA",
    "assets.fields.image": "URL SLIKE (NEOBVEZNO)",
    "assets.fields.imageOr": "— ALI NALOŽI —",
    "assets.fields.uploadPhoto": "Izberi iz galerije",
    "assets.fields.takePhoto": "Posnemi fotografijo",
    "assets.fields.uploading": "Nalaganje…",
    "assets.fields.notes": "OPOMBE",

    "journal.title": "Dnevnik plovb",
    "journal.subtitle": "KAPITANOV DNEVNIK",
    "journal.newEntry": "Nov vnos",
    "journal.filter.all": "Vse",
    "journal.filter.captain": "Kapitanov dnevnik",
    "journal.filter.fishing": "Ribolov",
    "journal.filter.excursion": "Izlet",
    "journal.filter.charter": "Čarter",
    "journal.filter.dockOvernight": "Prenočitev na privezu",
    "journal.empty.title": "Še ni vnosov.",
    "journal.deleteConfirm": "Izbrišem ta vnos? Shranjen bo 30 dni v Koš, preden bo trajno odstranjen.",
    "journal.deletedToast": "Vnos premaknjen v koš",
    "journal.restoredToast": "Vnos obnovljen",
    "journal.purgedToast": "Vnos trajno izbrisan",
    "journal.purgeConfirm": "TRAJNO izbrišem ta vnos ZDAJ? To dejanje je nepovratno.",
    "journal.purgeOn": "čistka",
    "journal.purgeNow": "Izbriši zdaj",
    "journal.restore": "Obnovi",
    "journal.trashBtn": "Koš",
    "journal.trashTitle": "IZBRISANI VNOSI",
    "journal.trashIntro": "Izbrisani vnosi se hranijo tukaj 30 dni. Lahko jih kadar koli obnovite; po 30 dneh so trajno odstranjeni.",
    "journal.savedToast": "Vnos shranjen",
    "journal.empty.sub": "Zabeležite prvo plovbo — vsak dnevnik krepi znanje o krajih.",
    "journal.logType.captains_log": "Kapitanov dnevnik",
    "journal.logType.fishing_log": "Ribolovni dnevnik",
    "journal.logType.excursion_log": "Izlet",
    "journal.logType.charter_log": "Čarter",
    "journal.logType.dock_overnight": "Prenočitev na privezu",
    "journal.metric.fuel": "GORIVO",
    "journal.metric.hrs": "URE",
    "journal.metric.sea": "MORJE",
    "journal.metric.fishing": "RIBOLOV",
    "journal.metric.species": "Vrsta",
    "journal.metric.bait": "Vaba",
    "journal.metric.method": "Metoda",
    "journal.metric.kept": "Obdržani",
    "journal.metric.released": "Izpuščeni",
    "journal.metric.rating": "Ocena",
    "journal.label.incidents": "Dogodki",
    "journal.label.dos": "Priporočeno",
    "journal.label.donts": "Ne počnite",
    "journal.label.restaurant": "RESTAVRACIJA",
    "journal.label.bar": "BAR",
    "journal.label.guestExperience": "Izkušnja gostov",
    "journal.label.photos": "FOTOGRAFIJE",
    "journal.untitled": "Neimenovana plovba",

    "logDialog.title": "Nov vnos v dnevnik",
    "logDialog.logType": "VRSTA DNEVNIKA",
    "logDialog.asset": "Sredstvo",
    "logDialog.date": "Datum",
    "logDialog.fromDate": "Datum od",
    "logDialog.toDate": "Datum do (neobvezno)",
    "logDialog.toDateHint": "Pustite prazno za eno noč. Nastavite poznejši datum, da zabeležite vsako noč bivanja.",
    "logDialog.skipperOverride": "ZABELEŽI V IMENU",
    "logDialog.skipperSelf": "Sebe (privzeto)",
    "logDialog.skipperOverrideHint": "Pripiši ta dnevnik drugi osebi — npr. bila je na pomolu, medtem ko je bila ladja pod vašo rezervacijo ali na servisu.",
    "logDialog.multiDaySaved": "Zabeleženih {count} noči na pomolu",
    "logDialog.linkedReservation": "POVEZANA REZERVACIJA (NEOBVEZNO)",
    "logDialog.tabs.basics": "Osnove",
    "logDialog.tabs.conditions": "Razmere",
    "logDialog.tabs.notes": "Opombe & ocene",
    "logDialog.tabs.photos": "Fotografije",
    "logDialog.tabs.fishing": "Ribolov",
    "logDialog.place": "Kraj",
    "logDialog.placePlaceholder": "Zaliv, marina, restavracija…",
    "logDialog.route": "Pot",
    "logDialog.routePlaceholder": "Koper → Piran → Portorož",
    "logDialog.fuel": "Gorivo (L)",
    "logDialog.engineHours": "Ure motorja",
    "logDialog.weather": "Vreme",
    "logDialog.weatherPlaceholder": "Sončno, 24°C, Z 8 vozlov",
    "logDialog.seaState": "Stanje morja",
    "logDialog.seaPlaceholder": "Mirno / 1-2 čevlja",
    "logDialog.incidents": "Dogodki",
    "logDialog.incidentsPlaceholder": "Karkoli vrednega opombe…",
    "logDialog.whatDo": "Kaj storiti",
    "logDialog.whatDont": "Česa NE storiti",
    "logDialog.restaurantReview": "Ocena restavracije",
    "logDialog.barReview": "Ocena bara",
    "logDialog.guestExperience": "Izkušnja gostov",
    "logDialog.outcome": "IZID",
    "logDialog.outcome.excellent": "odlično",
    "logDialog.outcome.good": "dobro",
    "logDialog.outcome.poor": "slabo",
    "logDialog.outcome.skunked": "brez ulova",
    "logDialog.species": "Vrsta",
    "logDialog.speciesPlaceholder": "Brancin, skuša…",
    "logDialog.method": "Metoda",
    "logDialog.methodPlaceholder": "Panula, jigging…",
    "logDialog.bait": "Vaba",
    "logDialog.kept": "Obdržani",
    "logDialog.released": "Izpuščeni",
    "logDialog.successRating": "OCENA USPEHA",
    "logDialog.saveEntry": "Shrani vnos",
    "logDialog.photosCount": "{count} fotografij(a)",
    "logDialog.photosDrop": "Klikni za pripenjanje fotografij (JPEG/PNG/WebP, do 15MB)",
    "logDialog.photosHint": "GPS iz EXIF se samodejno prebere. Lahko prepišeš spodaj.",
    "logDialog.tagAllMissing": "Označi vse manjkajoče z mojo lokacijo",
    "logDialog.caption": "Napis (neobvezno)",
    "logDialog.geoFromExif": "iz EXIF",
    "logDialog.geoDevice": "naprava",
    "logDialog.geoManual": "ročno",
    "logDialog.noLocation": "brez lokacije",
    "logDialog.useMyLocation": "Uporabi mojo lokacijo",

    "fishing.title": "Spomin ulova",
    "fishing.subtitle": "// RIBOLOVNA INTELIGENCA",
    "fishing.intro": "Dolgoročni podatki ulova, ki se kopičijo po sezonah — mesta, vrste, metoda, uspeh.",
    "fishing.stat.trips": "PLOVBE",
    "fishing.stat.landed": "UJETIH RIB",
    "fishing.stat.avgRating": "POVP. OCENA",
    "fishing.stat.species": "ZABELEŽENIH VRST",
    "fishing.avgRatingSub": "{excellent} odličnih · {good} dobrih",
    "fishing.empty": "Še ni ribolovnih dnevnikov. Začnite beležiti — znanje raste z vsako plovbo.",
    "fishing.speciesTracker": "SLEDENJE VRST",
    "fishing.hotSpots": "VROČE TOČKE",
    "fishing.timeline": "ČASOVNA OS",
    "fishing.trips": "plovb",
    "fishing.kept": "obdržani",
    "fishing.released": "izpuščeni",
    "fishing.visits": "obiskov",
    "fishing.fish": "rib",

    "map.title": "Foto zemljevid",
    "map.subtitle": "GEOOZNAČENI POSNETKI",
    "map.loading": "Nalagam oznake…",
    "map.status": "{count} fotografij(a) na zemljevidu. Kliknite sličico, da jo odprete.",
    "map.loadingTiles": "NALAGAM ZEMLJEVID…",
    "map.empty": "Še ni geooznačenih fotografij.",
    "map.emptyHint": "Naložite fotografije iz kapitanovega dnevnika z lokacijo, da se pripnejo tukaj.",
    "map.layer.map": "Zemljevid",
    "map.layer.satellite": "Satelit",
    "map.layer.terrain": "Teren",
    "map.layer.nautical": "Navtični",
    "map.sheet.viewJournal": "Odpri dnevnik",
    "map.sheet.captured": "posneto",
    "map.sheet.loadFail": "Slike ni bilo mogoče naložiti.",
    "map.sheet.deletePhoto": "Izbriši fotografijo",
    "map.sheet.deleteConfirm": "Izbrišem to fotografijo? Premeščena bo v koš in trajno odstranjena čez 30 dni.",
    "map.sheet.deleted": "Fotografija izbrisana.",

    "places.title": "Destinacije & nevarnosti",
    "places.subtitle": "ZNANJE O KRAJIH",
    "places.add": "Dodaj kraj",
    "places.searchLabel": "ISKANJE",
    "places.searchPlaceholder": "Ime, opombe, vrsta…",
    "places.radius": "POLMER · KM",
    "places.nearMe": "V bližini",
    "places.locating": "Iščem lokacijo…",
    "places.emptyNoSearch": "Še ni krajev. Začnite zarisovati poti.",
    "places.emptySearch": "Iskanju ne ustreza noben kraj.",
    "places.useMyLocation": "Uporabi mojo trenutno lokacijo",
    "places.latitude": "ZEM. ŠIRINA",
    "places.longitude": "ZEM. DOLŽINA",
    "places.type": "VRSTA",
    "places.notes": "OPOMBE",
    "places.do": "PRIPOROČENO",
    "places.dont": "NE POČNITE",
    "places.addPlace": "Dodaj kraj",
    "places.newReview": "Nova ocena",
    "places.addReview": "Dodaj oceno",
    "places.rating": "OCENA",
    "places.comment": "KOMENTAR",
    "places.reviewType": "VRSTA",
    "places.noReviews": "Še ni ocen.",
    "places.history": "ZGODOVINA OCEN · {count} vnosov",
    "places.reviews": "ocen",
    "places.distanceAway": "{km} km stran",
    "places.types.marina": "Marina",
    "places.types.bay": "Zaliv",
    "places.types.restaurant": "Restavracija",
    "places.types.bar": "Bar",
    "places.types.fishing_spot": "Ribolovno mesto",
    "places.types.hazard": "Nevarnost",
    "places.types.excursion_stop": "Postaja izleta",
    "places.types.anchorage": "Sidrišče",
    "places.types.berth": "Privez",
    "places.reviewTypes.restaurant": "restavracija",
    "places.reviewTypes.bar": "bar",
    "places.reviewTypes.berth": "privez",
    "places.reviewTypes.anchorage": "sidrišče",
    "places.reviewTypes.fishing": "ribolov",

    "commercial.title": "Čarter & izleti",
    "commercial.subtitle": "KOMERCIALNO POSLOVANJE",
    "commercial.stat.pending": "ČAKAJOČI",
    "commercial.stat.approved": "ODOBRENI",
    "commercial.stat.rejected": "ZAVRNJENI",
    "commercial.stat.revenue": "PRIHODEK",
    "commercial.tab.pending": "Čaka odobritev",
    "commercial.tab.upcoming": "Prihajajoči",
    "commercial.tab.completed": "Zaključeni",
    "commercial.tab.archive": "Zavrnjeni / preklicani",
    "commercial.empty": "V tej vrsti ni vnosov.",
    "commercial.voteHistory": "ZGODOVINA GLASOV",
    "commercial.contact": "KONTAKT",
    "commercial.notes": "OPOMBE",
    "commercial.votePlaceholder": "Opomba glasovanja (revizijska sled)…",
    "commercial.unnamedClient": "Neimenovana stranka",
    "commercial.votesOf": "{done}/2 glasov",
    "commercial.approve": "Odobri",
    "commercial.reject": "Zavrni",
    "commercial.pax": "{count} potnikov",
    "commercial.noPrice": "cena ni določena",

    "maint.title": "Vzdrževalni tiketi",
    "maint.subtitle": "SERVISNA PISARNA",
    "maint.new": "Nov tiket",
    "maint.tab.open": "Odprti / V teku",
    "maint.tab.done": "Rešeni / Zaprti",
    "maint.tab.all": "Vsi",
    "maint.empty": "Tukaj ni tiketov.",
    "maint.start": "Začni",
    "maint.resolve": "Reši",
    "maint.dialog.title": "Nov vzdrževalni tiket",
    "maint.dialog.asset": "SREDSTVO",
    "maint.dialog.titleField": "NASLOV",
    "maint.dialog.severity": "RESNOST",
    "maint.dialog.description": "OPIS",
    "maint.dialog.planned": "Načrtuj redni servis",
    "maint.dialog.plannedHint": "Vklopi za preventivni / načrtovani servis. Izberi predvideni datum.",
    "maint.dialog.scheduledFor": "PREDVIDENO ZA",
    "maint.dialog.scheduledEnd": "DATUM ZAKLJUČKA (NEOBVEZNO)",
    "maint.dialog.scheduledEndHint": "Opomnik za zapis se sproži na ta datum",
    "maint.completion.label": "KAJ JE BILO OPRAVLJENO",
    "maint.dialog.serviceProvider": "IZVAJALEC",
    "maint.plannedBadge": "načrtovano",
    "maint.reactiveBadge": "reaktivno",
    "maint.tab.planned": "Načrtovani servis",
    "maint.tab.reactive": "Reaktivno",
    "maint.blockAsset": "Blokiraj termin sredstva",
    "maint.blockAssetHint": "Ustvari rezervacijo za vzdrževanje in odstrani sredstvo iz razpoložljivosti za izbrano obdobje.",
    "maint.scheduled": "Predvideno {date}",
    "maint.confirmDelete": "Trajno izbrišem tiket? Revizijska sled se ohrani.",
    "maint.providerEmail": "E-POŠTA IZVAJALCA",
    "maint.providerPhone": "TELEFON IZVAJALCA",
    "maint.providerPhoneHint": "Oblika E.164 (+klicna). Uporabljeno za SMS opomnike.",
    "maint.autoReminder": "Samodejni opomniki (5 delovnih dni + 1 dan prej)",
    "maint.autoReminderHint": "SMS/e-pošto pošljemo izvajalcu samodejno ob 06:00 UTC. Za vsak kanal mora biti ustrezen kontakt izpolnjen.",
    "maint.sendReminder": "Pošlji opomnik",
    "maint.reminderLong": "Dolg opomnik (celotne podrobnosti)",
    "maint.reminderShort": "Kratek opomnik (dan prej, za potrditev)",
    "maint.reminderSent": "Opomnik poslan · {kind}",
    "maint.reminderFailed": "Opomnik ni uspel · poglej zgodovino",
    "maint.reminderPartial": "Opomnik delno · poglej zgodovino",
    "maint.autoOn": "samodejno",
    "maint.lastReminder": "Zadnji opomnik {kind} · {date}",
    "maint.noContactHint": "Dodaj e-pošto ali telefon izvajalca za omogočanje opomnikov.",
    "maint.reminderChannelSms": "SMS",
    "maint.reminderChannelEmail": "E-pošta",
    "maint.opened": "Odprt {date} · {by}",

    "invoices.title": "Računi",
    "invoices.subtitle": "RAČUNOVODSTVO",
    "invoices.intro": "Nalagajte, kategorizirajte in spremljajte, za kaj plačujete.",
    "invoices.new": "Zabeleži račun",
    "invoices.newDialog.title": "Zabeleži nov račun",
    "invoices.editDialog.title": "Uredi račun",
    "invoices.stat.total": "SKUPAJ",
    "invoices.stat.totalSub": "{count} računov",
    "invoices.stat.outstanding": "NEPORAVNANO",
    "invoices.stat.outstandingSub": "Neplačani + zapadli",
    "invoices.stat.overdue": "ZAPADLO",
    "invoices.stat.overdueSub": "Po roku plačila",
    "invoices.stat.categories": "KATEGORIJE",
    "invoices.stat.categoriesSub": "Aktivne oznake",
    "invoices.perPayer": "STROŠKI PO PLAČNIKU",
    "invoices.paidShort": "plačano",
    "invoices.unpaidShort": "neplačano",
    "invoices.unassigned": "Nedoločeno",
    "invoices.searchPlaceholder": "Dobavitelj, št. računa, opis…",
    "invoices.filter.status": "STANJE",
    "invoices.filter.category": "KATEGORIJA",
    "invoices.filter.paidBy": "PLAČAL",
    "invoices.empty": "Še ni računov. Naložite prvega.",
    "invoices.col.date": "DATUM",
    "invoices.col.vendor": "DOBAVITELJ",
    "invoices.col.description": "OPIS",
    "invoices.col.category": "KATEGORIJA",
    "invoices.col.paidBy": "PLAČAL",
    "invoices.col.amount": "ZNESEK",
    "invoices.col.status": "STANJE",
    "invoices.col.actions": "DEJANJA",
    "invoices.due": "do {date}",
    "invoices.markPaid": "Označi kot plačano",
    "invoices.openAttachment": "Odpri prilogo",
    "invoices.confirmDelete": "Trajno izbrišem ta račun?",
    "invoices.dialog.vendor": "DOBAVITELJ *",
    "invoices.dialog.invoiceNum": "ŠT. RAČUNA",
    "invoices.dialog.amount": "ZNESEK *",
    "invoices.dialog.currency": "VALUTA",
    "invoices.dialog.date": "DATUM *",
    "invoices.dialog.category": "KATEGORIJA",
    "invoices.dialog.status": "STANJE",
    "invoices.dialog.dueDate": "ROK PLAČILA",
    "invoices.dialog.asset": "SREDSTVO",
    "invoices.dialog.paidBy": "PLAČAL (ZA SLEDENJE STROŠKOV)",
    "invoices.dialog.paidByNone": "— ni določeno —",
    "invoices.dialog.owners": "Lastniki",
    "invoices.dialog.team": "Ekipa",
    "invoices.dialog.description": "ZA KAJ GRE? *",
    "invoices.dialog.descPlaceholder": "Opišite, za kaj je ta račun…",
    "invoices.dialog.attachment": "PRILOGA (PDF / SLIKA)",
    "invoices.dialog.attachHint": "Klikni za pripenjanje PDF ali slike (do 15MB)",
    "invoices.dialog.attachExists": "Priloga že obstaja · klikni za zamenjavo",
    "invoices.dialog.viewExisting": "Odpri obstoječo prilogo",
    "invoices.categories.fuel": "gorivo",
    "invoices.categories.maintenance": "vzdrževanje",
    "invoices.categories.insurance": "zavarovanje",
    "invoices.categories.dockage": "privez",
    "invoices.categories.provisions": "živila",
    "invoices.categories.supplies": "potrebščine",
    "invoices.categories.services": "storitve",
    "invoices.categories.commercial": "komercialno",
    "invoices.categories.other": "drugo",

    "documents.title": "Dokumenti & rok veljavnosti",
    "documents.subtitle": "SKLADNOST",
    "documents.new": "Naloži dokument",
    "documents.pageNew": "NOVO",
    "documents.empty": "Še ni dokumentov. Naložite licence, zavarovanje, registracije.",
    "documents.col.title": "NAZIV",
    "documents.col.type": "VRSTA",
    "documents.col.asset": "SREDSTVO",
    "documents.col.expiry": "VELJAVNOST",
    "documents.col.uploaded": "NALOŽENO",
    "documents.dialog.title": "Naloži dokument",
    "documents.dialog.editTitle": "Uredi dokument",
    "documents.dialog.titleField": "NAZIV",
    "documents.dialog.titleRequired": "Naziv je obvezen",
    "documents.dialog.type": "VRSTA",
    "documents.dialog.asset": "SREDSTVO",
    "documents.dialog.expiry": "DATUM VELJAVNOSTI",
    "documents.dialog.file": "DATOTEKA (NEOBVEZNO)",
    "documents.dialog.pages": "STRANI",
    "documents.dialog.addPage": "Dodaj stran",
    "documents.dialog.dropHint": "Povleci in spusti datoteke sem ali klikni za izbiro. PDF in fotografije dobrodošli.",
    "documents.dialog.acceptTypes": "PDF · JPG · PNG · HEIC · HEIF",
    "documents.dialog.reorderHint": "Povleci strani z ročajem ⋮⋮ za prerazporeditev.",
    "documents.dialog.dragToReorder": "Povleci za prerazporeditev",
    "documents.pages": "strani",
    "documents.pageNum": "Stran {n}",
    "documents.dragHint": "Namig — spusti datoteke kjerkoli na strani za nov dokument, ali klikni ✏️ na obstoječi vrstici za dodajanje strani.",
    "documents.savedToast": "Dokument shranjen",
    "documents.updatedToast": "Dokument posodobljen",
    "documents.pageDrop.title": "Spusti datoteke za nov dokument",
    "documents.pageDrop.hint": "Mešanica PDF-jev in fotografij se združi v en večstranski dokument.",
    "documents.noFile": "Ta dokument še nima strani.",
    "documents.emailDocument": "Pošlji ta dokument po e-pošti",
    "documents.email.title": "Pošlji dokument",
    "documents.email.summary": "PRILOGA",
    "documents.email.toLabel": "PREJEMNIK",
    "documents.email.subjectLabel": "ZADEVA",
    "documents.email.messageLabel": "SPOROČILO (NEOBVEZNO)",
    "documents.email.messagePlaceholder": "Pozdravljeni, v prilogi pošiljam dokument…",
    "documents.email.send": "Pošlji",
    "documents.email.sending": "Pošiljam…",
    "documents.email.sentToast": "Dokument poslan",
    "documents.email.invalidEmail": "Vnesite veljaven e-naslov",

    // Poškodbe
    "damage.subtitle": "// SLEDENJE POŠKODBAM",
    "damage.title": "Poročila o poškodbah",
    "damage.intro": "Zajemi poškodbo s fotografijami in opisom, nato pa jo pretvori v e-pošto za popravilo in/ali servisni nalog — vse na enem mestu.",
    "damage.new": "Novo poročilo",
    "damage.empty": "Še ni poročil o poškodbah — držimo pesti.",
    "damage.deleteConfirm": "Izbrišem to poročilo? Šlo bo v koš in se samodejno počisti v 30 dneh.",
    "damage.deletedToast": "Poročilo izbrisano",
    "damage.savedToast": "Poročilo shranjeno",
    "damage.updatedToast": "Poročilo posodobljeno",
    "damage.linkedTicket": "Povezan servisni nalog:",
    "damage.orderRepair": "Naroči popravilo",
    "damage.filter.asset": "Sredstvo",
    "damage.filter.status": "Stanje",
    "damage.severity.low": "Nizka",
    "damage.severity.medium": "Srednja",
    "damage.severity.high": "Visoka",
    "damage.severity.critical": "Kritična",
    "damage.status.open": "Odprto",
    "damage.status.quoted": "Ponudba",
    "damage.status.in_progress": "V teku",
    "damage.status.repaired": "Popravljeno",
    "damage.status.closed": "Zaključeno",
    "damage.dialog.title": "Novo poročilo o poškodbi",
    "damage.dialog.editTitle": "Uredi poročilo",
    "damage.dialog.assetLabel": "SREDSTVO",
    "damage.dialog.assetRequired": "Najprej izberite sredstvo",
    "damage.dialog.titleLabel": "KAJ SE JE ZGODILO",
    "damage.dialog.titlePlaceholder": "Razpoka v desnem trupu blizu vodne linije",
    "damage.dialog.titleRequired": "Vnesite kratek naslov",
    "damage.dialog.severityLabel": "RESNOST",
    "damage.dialog.location": "Mesto na sredstvu",
    "damage.dialog.locationPlaceholder": "Desni trup, blizu krme, ~30cm pod vodno linijo",
    "damage.dialog.dateObserved": "Datum opažanja",
    "damage.dialog.description": "OPIS",
    "damage.dialog.descriptionPlaceholder": "Opišite kaj ste videli, kdaj se je zgodilo (če veste) in morebitne nujne pomisleke.",
    "damage.dialog.descRequired": "Potreben je kratek opis",
    "damage.dialog.photos": "FOTOGRAFIJE",
    "damage.dialog.photoCount": "fotografije",
    "damage.dialog.photosRequired": "Potrebna je vsaj ena fotografija",
    "damage.dialog.addPhoto": "Dodaj fotografijo",
    "damage.dialog.dropHint": "Spustite fotografije sem ali kliknite za izbiro. Iz galerije & HEIC sprejeto.",
    "damage.order.title": "Naroči popravilo",
    "damage.order.emailHeader": "POŠLJI E-POŠTO SERVISERJU",
    "damage.order.ticketHeader": "USTVARI TUDI SERVISNI NALOG",
    "damage.order.scheduledFor": "PLANIRAN DATUM (NEOBVEZNO)",
    "damage.order.providerPhone": "TELEFON SERVISERJA (ZA SMS OPOMNIKE)",
    "damage.order.messagePlaceholder": "Pozdravljeni, lahko prosim pošljete ponudbo in najzgodnejši možen termin za priloženo popravilo?",
    "damage.order.submit": "Pošlji",
    "damage.order.nothingToDo": "Dodajte e-pošto ali izberite 'ustvari servisni nalog' — vsaj eno dejanje je potrebno",
    "damage.order.toastEmailed": "poslano",
    "damage.order.toastTicket": "ustvarjen nalog",
    "damage.order.toastDone": "Opravljeno",

    // Dostopnost (velikost besedila)
    "a11y.cycle": "Velikost besedila",
    "a11y.size.normal": "Običajna",
    "a11y.size.large": "Veliko besedilo",
    "a11y.size.xl": "Zelo veliko besedilo",

    // Manjkajoči dnevniki — hitra akcija na nadzorni plošči
    "dashboard.missingLogs.title": "Pretekle vožnje brez dnevnika",
    "dashboard.missingLogs.intro": "Te odobrene rezervacije še nimajo kapitanovega dnevnika. Tapnite eno za vpis.",
    "dashboard.missingLogs.empty": "Vse je urejeno — vsaka pretekla vožnja je zabeležena. 🙌",
    "dashboard.missingLogs.logBtn": "Vpiši to vožnjo",
    "dashboard.missingLogs.viewAll": "Prikaži vse",
    "dashboard.missingLogs.daysAgo": "pred {n}d",
    "dashboard.missingLogs.clickHint": "Kliknite za pregled in vpis",

    "reports.title": "Operativna poročila",
    "reports.email.button": "Pošlji poročilo lastnikoma",
    "reports.email.sending": "Pošiljanje…",
    "reports.email.sent": "Poročilo poslano na {emails}",
    "reports.email.partial": "Delno poslano — dostavljeno na {emails}",
    "reports.email.failed": "Poročila ni bilo mogoče poslati",
    "reports.season": "SEZONA {year}",
    "reports.seasonal.heading": "SEZONSKA RAZDELITEV",
    "reports.seasonal.sub": "Zasebna raba lastnikov · {start} → {end}",
    "reports.seasonal.metDays": "14 dni izpolnjeno",
    "reports.seasonal.belowDays": "pod 14 dni",
    "reports.seasonal.days": "DNI",
    "reports.seasonal.weekends": "VIKENDI",
    "reports.seasonal.longest": "NAJDLJE",
    "reports.revenue.heading": "KOMERCIALNI PRIHODEK",
    "reports.revenue.count": "{count} zaključenih / odobrenih plovb",
    "reports.revenue.none": "Še ni komercialnega prihodka.",
    "reports.missing.heading": "MANJKAJOČI DNEVNIKI",
    "reports.missing.sub": "Pretekle rezervacije brez kapitanovega dnevnika",
    "reports.audit.heading": "REVIZIJSKA ČASOVNICA",
    "reports.audit.limited": "Pogled revizije je omejen ali prazen. Prijavite se kot direktor / skrbnik za celotno sled.",

    "settings.title": "Nastavitve",
    "settings.subtitle": "KONFIGURACIJA",
    "settings.agreement": "PARAMETRI DOGOVORA",
    "settings.param.split": "Delitev lastništva",
    "settings.param.splitValue": "Rok Kobal 50% · Borut Krzic 50%",
    "settings.param.season": "Poletna sezona",
    "settings.param.seasonValue": "01 jun → 30 sep",
    "settings.param.protected": "Min. zaščiteni termin",
    "settings.param.protectedValue": "14 zaporednih dni na lastnika",
    "settings.param.approval": "Komercialna odobritev",
    "settings.param.approvalValue": "Dvojni direktor (oba morata odobriti)",
    "settings.param.bareboat": "Najem brez skiperja",
    "settings.param.bareboatValue": "NI DOVOLJENO",
    "settings.param.excursion": "Skiper na izletu",
    "settings.param.excursionValue": "Obvezen · gorivo se zaračuna stranki",
    "settings.param.deciding": "Odločilni glas",
    "settings.param.decidingValue": "Rok Kobal — ne more preklicati zaščitenih terminov",
    "settings.param.holiday": "Prvomajski praznik",
    "settings.param.holidayValue": "Letno izmenično med lastnikoma",
    "settings.team": "EKIPA ({count})",
    "settings.col.name": "IME",
    "settings.col.email": "E-POŠTA",
    "settings.col.role": "VLOGA",
    "settings.col.status": "STANJE",
    "settings.active": "aktivno",
    "settings.inactive": "neaktivno",

    // Priročnik
    "manual.subtitle": "// UPORABNIŠKI PRIROČNIK",
    "manual.title": "Kako deluje LaSovita Ledger",
    "manual.intro": "Vloge, postopki, pravila, izračuni analitike, obvestila in odpravljanje težav — enotni vir resnice za vse uporabnike platforme.",
    "manual.downloadPdf": "Prenesi PDF",
    "manual.emailMe": "Pošlji mi po e-pošti",
    "manual.emailSent": "Priročnik poslan",
  },
};

const STORAGE_KEY = "lasovita.lang";

const I18nContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "en"; } catch { return "en"; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
    try { document.documentElement.lang = lang; } catch {}
  }, [lang]);

  const setLang = useCallback((l) => setLangState(l === "sl" ? "sl" : "en"), []);

  const t = useCallback((key, vars) => {
    const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
    let s = dict[key];
    if (s == null) s = TRANSLATIONS.en[key] ?? key;
    if (vars) {
      Object.keys(vars).forEach(k => { s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(vars[k])); });
    }
    return s;
  }, [lang]);

  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export function useT() {
  const { t } = useContext(I18nContext);
  return t;
}

export function useLang() { return useContext(I18nContext); }

export const SUPPORTED_LANGS = [
  { code: "en", label: "English", short: "EN" },
  { code: "sl", label: "Slovenščina", short: "SL" },
];
